import React, {
    useCallback,
    useRef,
    createRef,
    useMemo,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react';

import { createUseStyles } from '@Sage/hoi-poi-ui';
import { isEqual, setupCurrencyLocaleUser } from '@Sage/ssm-utils';
import {
    AlignedGridsModule,
    AutoGenerateColumnsModule,
    BigIntFilterModule,
    CellApiModule,
    CellSpanModule,
    CellStyleModule,
    CheckboxEditorModule,
    ClientSideRowModelApiModule,
    ClientSideRowModelModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    ColumnHoverModule,
    CsvExportModule,
    CustomEditorModule,
    CustomFilterModule,
    DateEditorModule,
    DateFilterModule,
    DragAndDropModule,
    EventApiModule,
    ExternalFilterModule,
    GridStateModule,
    HighlightChangesModule,
    InfiniteRowModelModule,
    LargeTextEditorModule,
    LocaleModule,
    ModuleRegistry,
    NumberEditorModule,
    NumberFilterModule,
    PaginationModule,
    PaginationPageNumbersModule,
    PinnedRowModule,
    QuickFilterModule,
    RenderApiModule,
    RowApiModule,
    RowAutoHeightModule,
    RowDragModule,
    RowSelectionModule,
    RowStyleModule,
    ScrollApiModule,
    SelectEditorModule,
    TextEditorModule,
    TextFilterModule,
    TooltipModule,
    UndoRedoEditModule,
    ValueCacheModule,
    enableDevValidations,
    provideGlobalGridOptions,
} from 'ag-grid-community';
// Official structural stylesheet (layout/positioning only — no colours). Keeps the grid
// DOM layout correct for the installed AG Grid version so it does not depend on the
// hand-rolled structural CSS staying in sync with AG Grid's DOM. The balham look is still
// provided by the scoped JSS in ./styles, which is more specific and wins for cosmetics.
// AG Grid marks these CSS files as side effects, so the import is not tree-shaken.
import 'ag-grid-community/styles/ag-grid.css';
import { AgGridReact } from 'ag-grid-react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';

import { publish } from 'lib/EventBuser';
import { DATA_GRID_REFRESH_TOTAL } from 'lib/events';
import { useWeb5Context } from 'lib/Web5Context';
import { getSvgSelected, getSvgNonSelected } from 'utils/domElements';
import { addRequestToQueue, addToRowsBeforeDrag, processDragQueue } from 'utils/requests';

import ModalOptions from '../ModalOptions';
import AvatarCell from './cells/AvatarCell';
import { isDataGridCellNestedInteraction } from './utils/isDataGridCellNestedInteraction';
import { attachColDefCellClickHandler } from './utils/cellClick';
import AvatarCellWithSyncStatus from './cells/AvatarCellWithSyncStatus';
import BooleanCell from './cells/BooleanCell';
import BooleanIconCell from './cells/BooleanIconCell';
import CurrencyCell from './cells/CurrencyCell';
import DateCell from './cells/DateCell';
import FilterCell from './cells/FilterCell';
import FullWidthDividerCell from './cells/FullWidthDividerCell';
import IconCell from './cells/IconCell';
import LoadingCell from './cells/LoadingCell';
import MultiValueCell from './cells/MultiValueCell';
import MultiSelectReadCell from '../SectionListCrud/editCells/MultiSelectReadCell';
import NumberCell from './cells/NumberCell';
import PercentageCell from './cells/PercentageCell';
import PercentageTextCell from './cells/PercentageTextCell';
import RowNumberCell from './cells/RowNumberCell';
import StepCell from './cells/StepCell';
import TagCell from './cells/TagCell';
import TextCell from './cells/TextCell';
import TextTwoValuesCell from './cells/TextTwoValuesCell';
import ToggleCell from './cells/ToggleCell';
import ValuePercentageCell from './cells/ValuePercentageCell';
import { removeChildrenOnDrag } from './cells/components/GroupChevron';
import Actions from './components/Actions';
import MenuOptions from './components/MenuOptions';
import Popover from './components/Popover';
import ToolTip from './components/Tooltip';
import {
    getAgGridColumnId,
    getAgGridColumnWidth,
    getColDefContextValue,
    getResizedColumnsFromEvent,
    getLastDataColumnIndex,
    getLastVisibleDataColumnIndex,
    hasExplicitColumnWidthsFromDefs,
    preventAutoUnpinColumnsOnResize,
    hasVisibleFieldSelectorColumn,
    getFieldSelectorSpacerColDef,
    applyFieldSelectorSpacerColumnState,
    FIELD_SELECTOR_SPACER_COL_ID,
    applyUserColumnWidthToGridColDef,
    syncUserColumnWidthsToGridColDefs,
    hideGridOverlay,
    isRowDragEnabled,
    sanitizeColDefForAgGrid,
    sanitizeGridOptionsForAgGrid,
    coerceNumericGridOption,
    shouldSuppressColumnVirtualisation,
    applyColumnMoveToIndex,
    resolveDefaultAgGridRowId,
    toAgGridRowId,
} from './utils/agGridColumn';
import {
    hasPaintedGridBodyRows,
    queryGridCenterRowsContainer,
    waitForGridBodyRowsPainted,
} from './utils/agGridDomSelectors';
import {
    hasExpandedGroupRows,
    getParentRowIds,
    mergeParentRowsPreservingExpansions,
} from './utils/groupRowExpansion';
import { mergeColumnRowState } from './utils/actionsOverlay';
import TextFilter from './filters/TextFilter';
import HeaderBlankCell from './headerCells/HeaderBlankCell';
import HeaderIconCell from './headerCells/HeaderIconCell';
import HeaderTextCell from './headerCells/HeaderTextCell';
import { refreshHeaderCheckbox } from './headerCells/components/HeaderCheckbox';
import LoadingOverlay from './LoadingOverlay';
import NoRowsOverlay from './NoRowsOverlay';
import styles from './styles/index';

// AG Grid v33+ requires modules to be registered before any grid is created. This
// runs at module load and DataGrid is the only grid host, so registration is
// guaranteed before render. It is defined inline (not a side-effect-only import)
// because ssm-web5 sets "sideEffects": false, which would tree-shake a bare import.
//
// This is the full set of community modules that `AllCommunityModule` bundles in
// v36 MINUS dev-only validation (see enableDevValidations below). Listing them explicitly
// keeps every feature AllCommunityModule provided, but drops the dev-only validation
// code from production bundles. Keep this list in sync with `AllCommunityModule` when
// upgrading AG Grid: any new module it adds must be added here too.
ModuleRegistry.registerModules([
    AlignedGridsModule,
    AutoGenerateColumnsModule,
    BigIntFilterModule,
    CellApiModule,
    CellSpanModule,
    CellStyleModule,
    CheckboxEditorModule,
    ClientSideRowModelApiModule,
    ClientSideRowModelModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    ColumnHoverModule,
    CsvExportModule,
    CustomEditorModule,
    CustomFilterModule,
    DateEditorModule,
    DateFilterModule,
    DragAndDropModule,
    EventApiModule,
    ExternalFilterModule,
    GridStateModule,
    HighlightChangesModule,
    InfiniteRowModelModule,
    LargeTextEditorModule,
    LocaleModule,
    NumberEditorModule,
    NumberFilterModule,
    PaginationModule,
    PaginationPageNumbersModule,
    PinnedRowModule,
    QuickFilterModule,
    RenderApiModule,
    RowApiModule,
    RowAutoHeightModule,
    RowDragModule,
    RowSelectionModule,
    RowStyleModule,
    ScrollApiModule,
    SelectEditorModule,
    TextEditorModule,
    TextFilterModule,
    TooltipModule,
    UndoRedoEditModule,
    ValueCacheModule,
]);

// The DataGrid theme is hand-rolled in JSS targeting `.ag-theme-balham`. Opt out of
// the new Theming API (default since v33) so AG Grid does not inject its own CSS and
// override our custom styles.
provideGlobalGridOptions({ theme: 'legacy' });

// Dev-only configuration diagnostics; excluded from production bundles.
// Console only — the in-grid overlay is too noisy in local lists.
if (process.env.NODE_ENV !== 'production') {
    enableDevValidations({ showOverlayOn: [] });
}

const useStyles = createUseStyles(styles, 'DataGrid');

// Enabling input navigation
const suppressNavigation = (params) => {
    return params.event.key === 'Tab' || params.event.key?.includes('Arrow');
};

const defaultOptions = {
    // Use legacy theming so AG Grid does not inject its own CSS (new Theming API is the
    // v33+ default). Set per-grid as well as globally to guarantee it is applied even if
    // the global default is registered after this grid is created.
    theme: 'legacy',
    // AG Grid v31+ animates rows by default; that makes column/row layout visibly slow on
    // load and resize. Keep it off (drag tables re-enable it via affectedByDrag).
    animateRows: false,
    rowHeight: 50,
    headerHeight: 40,
    floatingFiltersHeight: 40,
    enableCellTextSelection: true,
    suppressCellFocus: true,
    suppressMovableColumns: true,
    suppressClickEdit: true,
    suppressScrollOnNewData: true,
    suppressContextMenu: true,
    suppressDragLeaveHidesColumns: true,
    // Pinned sections wider than the viewport trigger keepPinnedColumnsNarrowerThanViewport
    // (unpin/repin loop on resize). Horizontal scroll is the correct overflow behaviour.
    processUnpinnedColumns: preventAutoUnpinColumnsOnResize,
    alwaysShowHorizontalScroll: true,
    defaultColDef: {
        resizable: true,
        floatingFilter: false,
        suppressKeyboardEvent: suppressNavigation,
    },
    columnDefs: [],
    rowData: [],
    suppressTouch: true,
};

const buildColumnVisibilitySignature = (columnDefs, getHide) => {
    if (!columnDefs?.length) return '';

    return columnDefs
        .filter((col) => col.colId && col.colId !== FIELD_SELECTOR_SPACER_COL_ID)
        .map((col) => `${col.colId}:${getHide(col) ? 1 : 0}`)
        .join('|');
};

function DataGrid({
    id,
    className,
    dataSource,
    isLoading,
    rowData,
    totalRowData,
    hasError,
    emptyViewProps,
    errorViewProps,
    customEmptyViewComponent,
    initTable,
    initOnReady,
    useLazyLoad,
    infiniteScrolling,
    useLazyWithSections,
    pageSize,
    infiniteInitialRowCount,
    defaultColDef = {},
    columnDefs,
    useDragRows = false,
    useDragCustom = false,
    useDragColumns = false,
    customDragPreview,
    previewKey,
    getCustomDragInfo,
    canDrag,
    canDrop,
    onDragDrop,
    saveColumnsOrder,
    savePinnedColumns,
    saveColumnSize,
    saveVisibleColumns,
    useSort,
    getInitialSortInfo,
    changeSortDirection,
    onCancelGroupRequests,
    dragGroups,
    onDragEnter,
    onDragEnd,
    columnsRefreshOnDragEnd,
    onClickRow,
    fullWidthCellRenderer,
    fieldSelector,
    fieldSelectorProps,
    /** When confirming column visibility, copy visibility from each key to linked colIds (e.g. discount1 → discount1Value). */
    fieldSelectorLinkedColumnIds,
    activeRowId,
    checkedRows,
    disabledCheckboxes,
    getCheckedRows,
    favoriteRows,
    disabledFavorites,
    getFavoriteRows,
    actions,
    hideActions,
    useFullWidthDivider,
    customIsFullWidthCell,
    getForceRedrawRows,
    addDropZones,
    showGhostWhenDrag = false,
    onRef,
    rowClass = '',
    rowClassRules = {},
    getTotal,
    forceGetTotal,
    locale,
    modalOptionsProps = {},
    useLoaderPlaceholder,
    loaderPlaceholderRowHeight,
    loaderPlaceholderRows,
    loaderPlaceholderColumns,
    useLoaderPlaceholderFullHeight,
    useLoaderFullHeight,
    placeholderOptions,
    customButtonLiteral: _customButtonLiteral,
    tooltipMenuContent,
    intercomRef,
    forceActionsRefresh,
    getRowNodeId,
    withoutOffset,
    forceRowUpdating,
    vanillaComponents = {},
    components = {},
    ...props
}) {
    //For lazyLoad --> Add fullWidthRows to the total, so the table will consider those rows
    // as well to the total rows to be printed.
    const getTotalWithFullWidthRows = useCallback(
        (rowData, totalData) => {
            if (!rowData) return totalData || 0;
            if (!useFullWidthDivider) return totalData || rowData.length;

            const fullWidthRows = rowData.filter((current) => current.isFullWidth);
            let totalFullWidthRows = fullWidthRows.length;
            if (totalData) return totalData + totalFullWidthRows;
            else return rowData.length;
        },
        [useFullWidthDivider],
    );

    //For general use --> Removes fullWidthRows from total, so them won't be counted
    // to show the total in the header info
    const getTotalWithoutFullWidthRows = useCallback(
        (rowData, totalData) => {
            if (!rowData) return totalData || 0;
            if (!useFullWidthDivider) return totalData || rowData.length;

            const fullWidthRows = rowData.filter((current) => current.isFullWidth);
            let totalFullWidthRows = fullWidthRows.length;
            return rowData.length - totalFullWidthRows;
        },
        [useFullWidthDivider],
    );

    // Lazy grids obtain their total from the backend response or getTotal. Their rowData is null
    // while AG Grid owns the rows, so it must not replace that total with a local zero on re-render.
    const shouldDeriveTotalFromRows = !useLazyLoad;
    const web5Context = useWeb5Context();
    const { t } = web5Context;
    const classes = useStyles();
    const isLazyLoadedRef = useRef(false);
    const isGridLoadingRef = useRef(false);
    const lazyLoadRequestVersionRef = useRef(0);
    const [isLazyLoaded, setIsLazyLoadedState] = useState(false);
    const setLazyLoaded = useCallback((value) => {
        isLazyLoadedRef.current = value;
        setIsLazyLoadedState(value);
    }, []);
    const isAutoHeightLayout = (props.domLayout ?? 'normal') === 'autoHeight';
    const pendingLazyLoad = useLazyLoad && !isLazyLoadedRef.current;
    // Only React-driven flags here — isGridLoadingRef is imperative (purge/sort races) and must
    // not keep isLoading on the root after empty/data settles (it hides the no-rows overlay).
    const showGridLoading = isLoading || pendingLazyLoad;
    const clearGridLoadingState = useCallback(() => {
        isGridLoadingRef.current = false;
        gridContext.current.isGridLoading = false;
        dataGridRef.current?.classList?.remove(classes.isLoading);
    }, [classes.isLoading]);
    const publishGridTotal = useCallback(
        (total, { force = false } = {}) => {
            const normalized =
                typeof total === 'number' && Number.isFinite(total) ? total : Number(total) || 0;
            const previousTotal = gridContext.current.totalData ?? 0;
            const isGridLoading =
                isGridLoadingRef.current || isLoading || (useLazyLoad && !isLazyLoadedRef.current);
            const shouldHoldPreviousTotal =
                !force && isGridLoading && normalized === 0 && previousTotal > 0;

            if (shouldHoldPreviousTotal) {
                return;
            }

            if (normalized > 0) {
                gridContext.current.stableHeaderTotal = normalized;
            } else if (!isGridLoading) {
                gridContext.current.stableHeaderTotal = 0;
            }

            gridContext.current.totalData = normalized;

            if (!force && previousTotal === normalized) {
                return;
            }

            publish(`${DATA_GRID_REFRESH_TOTAL}-${id}`, normalized);
        },
        [id, isLoading, useLazyLoad],
    );
    const [isInitialColumnLayout, setIsInitialColumnLayout] = useState(true);
    const dataGridRef = useRef(null);
    const pendingRevealCancelRef = useRef(null);

    const cancelPendingReveal = useCallback(() => {
        pendingRevealCancelRef.current?.();
        pendingRevealCancelRef.current = null;
    }, []);

    useEffect(() => () => cancelPendingReveal(), [cancelPendingReveal]);

    useEffect(() => {
        const root = dataGridRef.current;
        if (!root) return;

        // Legacy tooltips lived inside the grid root; remove them so only the body portal is used.
        // Body portals are React-owned (createPortal). Do not node.remove() them — that races
        // unmount and throws NotFoundError: removeChild.
        root.querySelectorAll('.w5-tooltip').forEach((node) => node.remove());
    }, []);

    const gridRef = useRef(null);
    const gridWidthRef = useRef(0);
    const userColumnWidthsRef = useRef({});
    const userResizedColumnIdsRef = useRef(new Set());
    const sizeColumnsToFitTimerRef = useRef(null);
    const initialColumnLayoutFrameRef = useRef(null);
    const apiRef = useRef(null);
    const isDragging = useRef(false);
    const gridApi = useRef(null);
    const gridColumnApi = useRef(null);
    const columnMovedId = useRef(null);
    const columnMovedToIndex = useRef(null);
    const componentsRef = useRef(components);
    componentsRef.current = components;
    const immutableRowData = useRef([]);
    const rowMoved = useRef(false);
    const immutableBeforeDrag = useRef([]);
    const isQueueProcessing = useRef(false);
    const [hoveredRowInfo, setHoveredRowInfo] = useState(null);
    const [committedRowData, setCommittedRowData] = useState(null);
    const updateTableDataRefs = useCallback((data) => {
        const nextData = Array.isArray(data) ? data : data;
        gridContext.current.tableData = nextData;
        immutableRowData.current = Array.isArray(nextData) ? [...nextData] : nextData;
    }, []);
    const commitGridRowData = useCallback(
        (data) => {
            const nextData = Array.isArray(data) ? data : data || [];
            updateTableDataRefs(nextData);
            setCommittedRowData(hasExpandedGroupRows(nextData) ? nextData : null);
        },
        [updateTableDataRefs],
    );
    const gridContext = useRef({
        tableId: id || '',
        tableRef: createRef(),
        totalData: totalRowData || getTotalWithoutFullWidthRows(rowData) || 0,
        stableHeaderTotal: totalRowData || getTotalWithoutFullWidthRows(rowData) || 0,
        checkboxData: checkedRows || null,
        favoriteData: favoriteRows || null,
        getHoveredRowInfo: setHoveredRowInfo,
        hasActions: actions && actions?.length > 0,
        tableData: rowData || [],
        tableDataForLazySections: [],
        updateTableDataRefs: (data) => updateTableDataRefs(data),
        commitGridRowData: (data) => commitGridRowData(data),
        getGridApi: () => gridApi.current,
        useDragCustom,
        canDrag,
        t,
        hasShowTotal: false,
        locale: locale || '',
        isGridLoading: false,
    });

    useEffect(() => {
        gridContext.current.updateTableDataRefs = updateTableDataRefs;
        gridContext.current.commitGridRowData = commitGridRowData;
        gridContext.current.getGridApi = () => gridApi.current;
    }, [updateTableDataRefs, commitGridRowData]);

    useEffect(() => {
        gridContext.current.isGridLoading = showGridLoading || isGridLoadingRef.current;
    }, [showGridLoading]);

    const oldInitialActiveRowId = useRef(null);
    const sortInfoForLazyLoad = useRef(null);
    const getInitialSortInfoRef = useRef(getInitialSortInfo);
    getInitialSortInfoRef.current = getInitialSortInfo;
    const stableGetInitialSortInfo = useCallback(() => getInitialSortInfoRef.current?.(), []);
    const [gridReadyEpoch, setGridReadyEpoch] = useState(0);
    const [hiddenColumns, setHiddenColumns] = useState(null);
    const defaultColumnMinWidth = 100;
    const lazyLoadInfo = useRef(null);
    const [mappedSchema, setMappedSchema] = useState([]);
    const [selectedFields, setSelectedFields] = useState({});
    const [columnLayoutEpoch, setColumnLayoutEpoch] = useState(0);
    const isFirstLoad = useRef(true);
    const oldSelectedFields = useRef({});
    const dragObserver = useRef();
    const dragTarget = useRef();
    const columnVisibilitySignatureRef = useRef('');
    const pendingColumnVisibilityRef = useRef(null);

    const completeInitialColumnLayout = useCallback(() => {
        cancelAnimationFrame(initialColumnLayoutFrameRef.current);
        initialColumnLayoutFrameRef.current = requestAnimationFrame(() => {
            initialColumnLayoutFrameRef.current = null;
            setIsInitialColumnLayout(false);
        });
    }, []);

    useEffect(() => () => cancelAnimationFrame(initialColumnLayoutFrameRef.current), []);

    const resolvedLocale = locale || web5Context.locale || '';

    const resolveColumnHide = useCallback((column) => {
        const colId = column?.colId || column?.field;
        if (!colId) return !!column?.hide;

        const pending = pendingColumnVisibilityRef.current;
        if (pending && Object.prototype.hasOwnProperty.call(pending, colId)) {
            return !pending[colId];
        }

        return !!column.hide;
    }, []);

    const hasNoVisibleFieldSelectorColumns =
        fieldSelector &&
        mappedSchema.length > 0 &&
        !hasVisibleFieldSelectorColumn(columnDefs, resolveColumnHide);
    const rootClassName = classnames(classes.root, className, 'ag-theme-balham', {
        [classes.hasFieldSelector]: fieldSelector,
        [classes.autoHeightRoot]: isAutoHeightLayout,
        [classes.isPositioningColumns]: isInitialColumnLayout,
        [classes.isLoading]: showGridLoading,
    });

    useEffect(() => {
        setupCurrencyLocaleUser(resolvedLocale);
    }, [resolvedLocale]);

    useEffect(() => {
        gridContext.current.t = t;
        gridContext.current.locale = resolvedLocale;
        gridContext.current.hasActions = Boolean(actions?.length);
        gridContext.current.rowHasActions = (data) => {
            if (!actions?.length || !data) return false;
            return actions.some((action) => action(data) != null);
        };
    }, [t, resolvedLocale, actions]);

    // We need immutable data for row drag and drop actions
    useEffect(() => {
        if (useLazyLoad) return;

        if (!rowData?.length) {
            immutableRowData.current = [];
            gridContext.current.tableData = rowData || [];
            setCommittedRowData(null);
            gridApi.current?.setGridOption('rowData', rowData || []);
            return;
        }

        if (hasExpandedGroupRows(gridContext.current.tableData)) {
            const incomingParentIds = getParentRowIds(rowData);
            const currentParentIds = getParentRowIds(gridContext.current.tableData);

            if (incomingParentIds === currentParentIds) {
                const merged = mergeParentRowsPreservingExpansions(
                    rowData,
                    gridContext.current.tableData,
                );
                const changedParents = merged.filter((row) => {
                    if (row?.isChildren || row?.isLoader) return false;
                    const previous = gridContext.current.tableData.find(
                        (current) =>
                            !current?.isChildren &&
                            !current?.isLoader &&
                            String(current?.id) === String(row?.id),
                    );
                    return previous !== row;
                });

                if (changedParents.length > 0) {
                    gridApi.current?.applyTransaction({ update: changedParents });
                }
                commitGridRowData(merged);
            } else {
                commitGridRowData(rowData);
                gridApi.current?.setGridOption('rowData', rowData);
            }
            return;
        }

        immutableRowData.current = [...rowData];
        gridContext.current.tableData = rowData;
        setCommittedRowData(null);
        if (
            shouldDeriveTotalFromRows &&
            (getTotalWithoutFullWidthRows(rowData) || forceGetTotal || totalRowData)
        ) {
            const previousTotal = gridContext.current.totalData;
            const currentTotal = getTotalWithoutFullWidthRows(rowData) || 0;

            if (previousTotal !== currentTotal) {
                publishGridTotal(currentTotal);
            }
        }
    }, [
        commitGridRowData,
        rowData,
        getTotalWithoutFullWidthRows,
        publishGridTotal,
        forceGetTotal,
        shouldDeriveTotalFromRows,
        totalRowData,
        useLazyLoad,
    ]);

    useEffect(() => {
        if (!shouldDeriveTotalFromRows) return;
        if (!gridContext.current.hasShowTotal && !forceGetTotal && !totalRowData) return;

        const previousTotal = gridContext.current.totalData;
        const currentTotal = getTotalWithoutFullWidthRows(rowData) || 0;

        if (previousTotal !== currentTotal) {
            publishGridTotal(currentTotal);
        }
    }, [
        rowData,
        getTotalWithoutFullWidthRows,
        forceGetTotal,
        totalRowData,
        publishGridTotal,
        shouldDeriveTotalFromRows,
    ]);

    const buildHiddenColumnsFromColumnDefs = useCallback(() => {
        if (!fieldSelector || !columnDefs?.length) return null;

        return columnDefs.reduce((obj, column) => {
            if (!getColDefContextValue(column, 'suppressFromFieldSelector')) {
                obj[column.colId] = {
                    name: getColDefContextValue(column, 'fieldSelectorLabel') ?? column.headerName,
                    hide: resolveColumnHide(column),
                };
            }
            return obj;
        }, {});
    }, [columnDefs, fieldSelector, resolveColumnHide]);

    const getColumnVisibilitySignature = useCallback(
        () => buildColumnVisibilitySignature(columnDefs, resolveColumnHide),
        [columnDefs, resolveColumnHide],
    );

    const getPropsColumnVisibilitySignature = useCallback(
        () => buildColumnVisibilitySignature(columnDefs, (col) => !!col.hide),
        [columnDefs],
    );

    const applyColumnVisibilityToGrid = useCallback(() => {
        if (!gridApi.current || !columnDefs?.length) return;

        const columnState = columnDefs
            .filter((col) => col.colId && col.colId !== 'fmFieldSelectorSpacer')
            .map((col) => ({
                colId: col.colId,
                hide: resolveColumnHide(col),
            }));

        if (!columnState.length) return;

        gridApi.current.applyColumnState({ state: columnState, applyOrder: false });
        setColumnLayoutEpoch((epoch) => epoch + 1);

        if (fieldSelector) {
            applyFieldSelectorSpacerColumnState(gridApi.current, columnDefs);
        }
    }, [columnDefs, fieldSelector, resolveColumnHide]);

    const syncHiddenColumns = useCallback(() => {
        const nextHiddenColumns = buildHiddenColumnsFromColumnDefs();
        if (!nextHiddenColumns) return;
        setHiddenColumns((prev) => (isEqual(prev, nextHiddenColumns) ? prev : nextHiddenColumns));
    }, [buildHiddenColumnsFromColumnDefs]);

    const syncColumnVisibilityFromProps = useCallback(() => {
        if (!fieldSelector) return;

        const propsSignature = getPropsColumnVisibilitySignature();
        const resolvedSignature = getColumnVisibilitySignature();

        if (pendingColumnVisibilityRef.current) {
            if (propsSignature === resolvedSignature) {
                pendingColumnVisibilityRef.current = null;
                columnVisibilitySignatureRef.current = propsSignature;
                syncHiddenColumns();
                return;
            }

            if (resolvedSignature !== columnVisibilitySignatureRef.current) {
                columnVisibilitySignatureRef.current = resolvedSignature;
                setColumnLayoutEpoch((epoch) => epoch + 1);
            }

            syncHiddenColumns();
            return;
        }

        if (propsSignature === columnVisibilitySignatureRef.current) {
            syncHiddenColumns();
            return;
        }

        columnVisibilitySignatureRef.current = propsSignature;
        applyColumnVisibilityToGrid();
        syncHiddenColumns();
    }, [
        fieldSelector,
        getColumnVisibilitySignature,
        getPropsColumnVisibilitySignature,
        applyColumnVisibilityToGrid,
        syncHiddenColumns,
    ]);

    useEffect(() => {
        syncColumnVisibilityFromProps();
    }, [syncColumnVisibilityFromProps]);

    // AG Grid v33+: showNoRowsOverlay() is a no-op while suppressNoRowsOverlay or loading is true.
    // Update the grid option synchronously and clear any loading overlay before showing the empty view.
    const showEmptyStateOverlay = useCallback(() => {
        if (!gridApi.current) return;
        clearGridLoadingState();
        dataGridRef.current?.classList?.add(classes.isEmpty);
        // AG Grid 36 ignores showNoRowsOverlay while its internal loading option is true.
        // Clearing only our React/ref state leaves the grid loading and the empty view hidden.
        gridApi.current.setGridOption('loading', false);
        hideGridOverlay(gridApi.current);
        gridApi.current.setGridOption('suppressNoRowsOverlay', false);
        gridApi.current.showNoRowsOverlay();
        // After tab remount / layout, AG Grid may need a second pass once the viewport has size.
        requestAnimationFrame(() => {
            if (!gridApi.current) return;
            gridApi.current.setGridOption('suppressNoRowsOverlay', false);
            gridApi.current.showNoRowsOverlay();
        });
    }, [classes.isEmpty, clearGridLoadingState]);

    const showLoadingStateOverlay = useCallback(() => {
        if (!gridApi.current) return;
        cancelPendingReveal();
        isGridLoadingRef.current = true;
        gridContext.current.isGridLoading = true;
        dataGridRef.current?.classList?.remove(classes.isEmpty);
        dataGridRef.current?.classList?.add(classes.isLoading);
        gridApi.current.setGridOption('suppressNoRowsOverlay', true);
        gridApi.current.setGridOption('loading', true);
    }, [cancelPendingReveal, classes.isEmpty, classes.isLoading]);

    const overlayInputsRef = useRef({});
    overlayInputsRef.current = {
        isLoading,
        rowData:
            committedRowData ??
            (hasExpandedGroupRows(gridContext.current.tableData)
                ? gridContext.current.tableData
                : rowData),
        useLazyLoad,
        isLazyLoaded: isLazyLoadedRef.current,
    };

    const syncGridOverlay = useCallback(() => {
        if (!gridApi.current) return;
        const {
            isLoading: loading,
            rowData: rows,
            useLazyLoad: lazyLoad,
            isLazyLoaded: lazyLoaded,
        } = overlayInputsRef.current;
        const pendingFetch = lazyLoad && !lazyLoaded;
        // A finished lazy page must drop the latch. Otherwise the skeleton stays after the fetch.
        if (!loading && lazyLoad && lazyLoaded && immutableRowData.current?.length > 0) {
            dataGridRef.current?.classList?.remove(classes.isEmpty);
            clearGridLoadingState();
            gridApi.current?.setGridOption('suppressNoRowsOverlay', true);
            gridApi.current?.setGridOption('loading', false);
            hideGridOverlay(gridApi.current);
            return;
        }

        const showLoading = loading || pendingFetch || (lazyLoad && isGridLoadingRef.current);

        if (gridContext.current.totalData === 0 && lazyLoad && lazyLoaded && !showLoading) {
            dataGridRef.current?.classList?.add(classes.isEmpty);
        } else {
            dataGridRef.current?.classList?.remove(classes.isEmpty);
        }

        if (showLoading) {
            showLoadingStateOverlay();
            return;
        }

        if (!lazyLoad) {
            if (rows?.length > 0) {
                if (!hasPaintedGridBodyRows(dataGridRef.current)) {
                    showLoadingStateOverlay();
                    cancelPendingReveal();
                    pendingRevealCancelRef.current = waitForGridBodyRowsPainted({
                        root: dataGridRef.current,
                        onReady: () => {
                            pendingRevealCancelRef.current = null;
                            clearGridLoadingState();
                            gridApi.current?.setGridOption('suppressNoRowsOverlay', true);
                            gridApi.current?.setGridOption('loading', false);
                            hideGridOverlay(gridApi.current);
                        },
                    });
                    return;
                }

                clearGridLoadingState();
                gridApi.current?.setGridOption('suppressNoRowsOverlay', true);
                gridApi.current?.setGridOption('loading', false);
                hideGridOverlay(gridApi.current);
            } else {
                showEmptyStateOverlay();
            }
            return;
        }

        if (lazyLoaded) {
            if (immutableRowData.current?.length > 0) {
                dataGridRef.current?.classList?.remove(classes.isEmpty);
                clearGridLoadingState();
                gridApi.current?.setGridOption('suppressNoRowsOverlay', true);
                gridApi.current?.setGridOption('loading', false);
                hideGridOverlay(gridApi.current);
            } else {
                showEmptyStateOverlay();
            }
        }
    }, [
        classes.isEmpty,
        cancelPendingReveal,
        clearGridLoadingState,
        showEmptyStateOverlay,
        showLoadingStateOverlay,
    ]);

    // Infinite row model: sort triggers a new block request that can be debounced (blockLoadDebounceMillis).
    // Before getRows runs, show the overlay immediately. Placeholder rows are hidden individually by rowClassRules.
    const onLazyLoadSortChanged = useCallback(() => {
        if (!useLazyLoad) return;
        setLazyLoaded(false);
        showLoadingStateOverlay();
        gridContext.current.startRow = 0;
        syncGridOverlay();
    }, [useLazyLoad, setLazyLoaded, showLoadingStateOverlay, syncGridOverlay]);

    // Lazy (infinite) tables: purge cache → getRows → settle the grid overlay in setGridDataSource.
    const refresh = useCallback(
        (lazyInfo) => {
            lazyLoadInfo.current = lazyInfo;
            if (lazyLoadInfo?.current?.resetOffset) {
                gridApi?.current?.ensureIndexVisible(0, 'top');
            }

            if (!useLazyLoad) {
                showLoadingStateOverlay();
                const startRow = lazyInfo?.resetOffset ? 0 : undefined;
                initTable?.(pageSize, startRow, null);
                return;
            }

            lazyLoadRequestVersionRef.current += 1;
            setLazyLoaded(false);
            showLoadingStateOverlay();
            gridApi?.current?.purgeInfiniteCache();
            syncGridOverlay();
        },
        [setLazyLoaded, useLazyLoad, initTable, pageSize, showLoadingStateOverlay, syncGridOverlay],
    );

    const getRowCheckedState = useCallback(
        (colId, rowId, areAllChecked) => {
            if (areAllChecked) return true;

            const columnCheckboxData = gridContext.current.checkboxData?.[colId];
            const rowStates = columnCheckboxData?.rows;

            if (useLazyLoad) {
                if (rowStates?.[rowId]) return true;
                if (typeof rowStates?.[rowId] === 'boolean' && !rowStates[rowId]) return false;
                if (columnCheckboxData?.lazyAreAllChecked) return true;
                return false;
            }

            return rowStates?.[rowId] || false;
        },
        [useLazyLoad],
    );

    const refreshCheckboxes = useCallback(
        (colId, areAllChecked) => {
            const tableId = id;
            const tableElement = document.getElementById(tableId);
            const checkboxCells = tableElement.querySelectorAll(`[col-id='${colId}']`);
            checkboxCells.forEach((cell) => {
                if (cell.classList.contains('w5-header')) return;
                const cellWrapper = cell.querySelector('.w5-cell');

                if (!cellWrapper) return;

                const rowId = cellWrapper.getAttribute('data-rowid');
                const cellCheckboxContainer = cellWrapper.querySelector('.w5-cell-checkbox');

                if (!cellCheckboxContainer) return;

                const isRowChecked = getRowCheckedState(colId, rowId, areAllChecked);

                const isVisible = cellCheckboxContainer.classList.contains(
                    'w5-cell-checkbox--visible',
                );
                const isHidden = cellCheckboxContainer.classList.contains(
                    'w5-cell-checkbox--hidden',
                );
                const isBlocked = cellCheckboxContainer.classList.contains(
                    'w5-cell-checkbox--blocked',
                );
                const isDisabled = cellCheckboxContainer.classList.contains(
                    'w5-cell-checkbox--disabled',
                );
                if (isVisible || isHidden) {
                    if (isRowChecked) {
                        cellCheckboxContainer.classList.remove('w5-cell-checkbox--hidden');
                        cellCheckboxContainer.classList.add('w5-cell-checkbox--visible');
                    } else {
                        cellCheckboxContainer.classList.remove('w5-cell-checkbox--visible');
                        cellCheckboxContainer.classList.add('w5-cell-checkbox--hidden');
                    }
                }

                const svgContainer = cellWrapper.querySelector('.w5-cell-checkbox__svg-container');
                if (!svgContainer || disabledCheckboxes?.[colId]?.[rowId] || isDisabled) return;

                if (isRowChecked) {
                    if (isBlocked) return;
                    const svgSelected = getSvgSelected(tableId, colId, rowId);
                    if (!svgSelected) return;
                    svgContainer.innerHTML = svgSelected;
                    cellCheckboxContainer.setAttribute('data-ischecked', true);
                } else {
                    const svgNonSelected = getSvgNonSelected(tableId, colId, rowId);
                    if (!svgNonSelected) return;
                    svgContainer.innerHTML = svgNonSelected;
                    cellCheckboxContainer.setAttribute('data-ischecked', false);
                }
            });
        },
        [disabledCheckboxes, getRowCheckedState, id],
    );

    const refreshHeaderCheckboxes = useCallback(
        (colId) => {
            refreshHeaderCheckbox(id, colId, gridContext.current);
        },
        [id],
    );

    const refreshAllVisibleCheckboxes = useCallback(() => {
        if (!gridContext.current?.checkboxData) return;

        Object.keys(gridContext.current.checkboxData).forEach((colId) => {
            refreshCheckboxes(
                colId,
                gridContext.current.checkboxData[colId]?.areAllChecked || false,
            );
            refreshHeaderCheckboxes(colId);
        });
    }, [refreshCheckboxes, refreshHeaderCheckboxes]);

    const clearCheckboxes = useCallback(() => {
        if (!gridContext?.current?.checkboxData) return;
        const newCheckboxData = Object.keys(gridContext.current.checkboxData).reduce(
            (obj, current) => {
                obj[current] = {
                    rows: {},
                    areAllChecked: false,
                    lazyAreAllChecked: false,
                };
                return obj;
            },
            {},
        );

        gridContext.current.checkboxData = newCheckboxData;
        Object.keys(newCheckboxData).forEach((current) => {
            refreshCheckboxes(current, false);
            refreshHeaderCheckboxes(current);
        });
    }, [refreshCheckboxes, refreshHeaderCheckboxes]);

    const newGetCheckedRows = useCallback(() => {
        if (!getCheckedRows) return;
        const newChecked = getCheckedRows(immutableRowData.current);

        if (!newChecked || Object.keys(newChecked).length === 0 || !gridColumnApi.current) {
            return;
        }

        if (!gridContext.current.checkboxData) {
            gridContext.current.checkboxData = {};
        }

        const finalChecked = gridContext.current.checkboxData;
        const changed = mergeColumnRowState(finalChecked, newChecked);

        if (!changed) return;

        const columns = Object.keys(newChecked).reduce((arr, current) => {
            const oldColumn = gridColumnApi.current.getColumn(current);
            if (oldColumn) arr.push(oldColumn);
            return arr;
        }, []);

        gridApi.current?.refreshCells({ columns, force: true });
        Object.keys(finalChecked).forEach((colId) => refreshHeaderCheckboxes(colId));
    }, [getCheckedRows, refreshHeaderCheckboxes]);

    const newGetFavoriteRows = useCallback(
        (rowData) => {
            if (!getFavoriteRows) return;

            const newFavorite = rowData
                ? getFavoriteRows(rowData)
                : getFavoriteRows(immutableRowData.current);

            if (!newFavorite || Object.keys(newFavorite).length === 0) return;

            if (!gridContext.current.favoriteData) {
                gridContext.current.favoriteData = {};
            }

            const finalFavorite = gridContext.current.favoriteData;
            const changed = mergeColumnRowState(finalFavorite, newFavorite);

            if (!changed) return;

            let columns;
            if (gridColumnApi.current) {
                columns = Object.keys(newFavorite).reduce((arr, current) => {
                    const oldColumn = gridColumnApi.current.getColumn(current);
                    if (oldColumn) arr.push(oldColumn);
                    return arr;
                }, []);
            }

            if (columns?.length) gridApi.current?.refreshCells({ columns, force: true });
        },
        [getFavoriteRows],
    );

    const updateRow = useCallback((id, fields) => {
        const rowNode = gridApi.current.getRowNode(String(id));
        if (!rowNode) return;

        let data = rowNode.data;
        Object.keys(fields).forEach((current) => {
            data[current] = fields[current];
        });

        gridApi.current.redrawRows({ rowNodes: [rowNode] });
    }, []);

    const refreshColumnByColId = useCallback((colIds) => {
        if (colIds?.length === 0) return;

        let columns = colIds.reduce((arr, current) => {
            const column = gridColumnApi?.current?.getColumn?.(current);
            if (column) arr.push(column);
            return arr;
        }, []);

        gridApi.current?.refreshCells({ columns, force: true });
    }, []);

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                refresh,
                clearCheckboxes,
                updateRow,
                updateFavoriteRow: newGetFavoriteRows,
                getApi: () => gridApi.current,
                refreshColumnByColId,
            });
    }, [onRef, refresh, clearCheckboxes, updateRow, newGetFavoriteRows, refreshColumnByColId]);

    //Updates favoriteRows and drag rows when receiving new rowData when the table is !useLazyLoad
    useEffect(() => {
        if (!rowData || rowData?.length === 0) return;
        if (useLazyLoad) return;
        newGetFavoriteRows(rowData);
    }, [newGetFavoriteRows, rowData, useLazyLoad]);

    // Control the overlays to display (overlay only — avoid sizeColumnsToFit here; it is expensive
    // and already runs from onGridSizeChanged / onFirstDataRendered).
    useLayoutEffect(() => {
        syncGridOverlay();
    }, [
        useLazyLoad,
        rowData,
        committedRowData,
        isLoading,
        isLazyLoaded,
        gridReadyEpoch,
        syncGridOverlay,
    ]);

    const setRowSelected = useCallback((activeRowId) => {
        if (!gridApi?.current) return;
        const activeRowKey = activeRowId != null ? String(activeRowId) : null;
        const currentlySelected = gridApi.current.getSelectedNodes()?.[0];
        if (currentlySelected?.id === activeRowKey) return;

        gridApi.current.deselectAll();
        if (activeRowKey) {
            const activeNode = gridApi.current.getRowNode(activeRowKey);
            if (activeNode) activeNode.setSelected(true);
        }
    }, []);

    useEffect(() => {
        setRowSelected(activeRowId);
    }, [activeRowId, setRowSelected]);

    useEffect(() => {
        if (checkedRows === null || checkedRows === undefined) return;

        const previousColumnIds = Object.keys(gridContext.current.checkboxData || {});
        gridContext.current.checkboxData = checkedRows;

        const columnIds = new Set([...previousColumnIds, ...Object.keys(checkedRows)]);
        columnIds.forEach((colId) => {
            refreshCheckboxes(colId, checkedRows[colId]?.areAllChecked || false);
            refreshHeaderCheckboxes(colId);
        });
    }, [checkedRows, refreshCheckboxes, refreshHeaderCheckboxes]);

    const syncDisabledCheckboxClasses = useCallback(() => {
        if (!id || !columnDefs?.length) return;

        const tableElement = document.getElementById(id);
        if (!tableElement) return;

        const checkboxColIds = columnDefs
            .filter((col) => col?.cellRendererParams?.checkbox)
            .map((col) => col.colId);

        checkboxColIds.forEach((colId) => {
            const disabledRows = disabledCheckboxes?.[colId] ?? {};

            tableElement.querySelectorAll(`[col-id='${colId}']`).forEach((cell) => {
                if (cell.classList.contains('w5-header')) return;

                const wrapper = cell.querySelector('.w5-cell');
                if (!wrapper) return;

                const rowId = wrapper.getAttribute('data-rowid');
                const checkbox = wrapper.querySelector('.w5-cell-checkbox');
                if (!checkbox || !rowId) return;

                checkbox.classList.toggle(
                    'w5-cell-checkbox--disabled',
                    Boolean(disabledRows[rowId]),
                );
            });
        });
    }, [columnDefs, disabledCheckboxes, id]);

    useEffect(() => {
        if (!gridApi.current || !gridColumnApi.current || !columnDefs?.length) return;

        const checkboxColumns = columnDefs.reduce((arr, col) => {
            if (col?.cellRendererParams?.checkbox) {
                const column = gridColumnApi.current.getColumn(col.colId);
                if (column) arr.push(column);
            }
            return arr;
        }, []);

        if (checkboxColumns.length) {
            gridApi.current.refreshCells({ columns: checkboxColumns, force: true });
        }

        syncDisabledCheckboxClasses();
        const frameId = requestAnimationFrame(() => syncDisabledCheckboxClasses());

        return () => cancelAnimationFrame(frameId);
        // Intentionally omit columnDefs — column identity is stable after the context-spread fix;
        // re-running on every columnDefs reference change forced full cell refresh.
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [disabledCheckboxes, syncDisabledCheckboxClasses]);

    // This informs us about the current sort direction that the header column is displaying
    // >> so we can retrieve the correct sort to backend
    const getSortInfoForLazyLoad = useCallback((sortInfo) => {
        sortInfoForLazyLoad.current = sortInfo;
    }, []);

    const finalizeEmptyLazyLoad = useCallback(
        (startRow) => {
            // An empty later block means end-of-data, not an empty table. Keep existing rows painted.
            if (startRow > 0) {
                gridApi.current?.setGridOption('suppressNoRowsOverlay', true);
                return;
            }

            clearGridLoadingState();
            setLazyLoaded(true);
            newGetFavoriteRows();
            newGetCheckedRows();
            publishGridTotal(0, { force: true });
            showEmptyStateOverlay();
        },
        [
            clearGridLoadingState,
            newGetFavoriteRows,
            newGetCheckedRows,
            publishGridTotal,
            setLazyLoaded,
            showEmptyStateOverlay,
        ],
    );

    //For lazyLoad we define the dataSource here
    const setGridDataSource = useCallback(() => {
        lazyLoadRequestVersionRef.current += 1;
        setLazyLoaded(false);
        showLoadingStateOverlay();
        const dataSource = {
            getRows: (params) => {
                let requestVersion = lazyLoadRequestVersionRef.current;
                if (!params.startRow) {
                    requestVersion += 1;
                    lazyLoadRequestVersionRef.current = requestVersion;
                }

                const isCurrentRequest = () => requestVersion === lazyLoadRequestVersionRef.current;
                let sortInfo = null;
                if (!params.startRow) {
                    setLazyLoaded(false);
                    showLoadingStateOverlay();
                } else {
                    gridApi.current.setGridOption('suppressNoRowsOverlay', true);
                }

                gridContext.current.startRow = params.startRow;

                if (sortInfoForLazyLoad.current) {
                    sortInfo = {
                        sortField:
                            sortInfoForLazyLoad.current.sortField ||
                            sortInfoForLazyLoad.current.colId,
                        sortDir: sortInfoForLazyLoad.current.sort,
                    };
                }

                let startRow = 0;
                if (lazyLoadInfo?.current?.resetOffset) {
                    startRow = 0;
                    lazyLoadInfo.current = null;
                } else startRow = params.startRow || 0;

                function innerGetTotal() {
                    // Only first time

                    if (
                        getTotal &&
                        startRow === 0 &&
                        (gridContext.current.hasShowTotal || forceGetTotal)
                    ) {
                        return getTotal()
                            .then((total) => {
                                if (isCurrentRequest()) publishGridTotal(total);
                            })
                            .catch(() => {
                                if (isCurrentRequest()) publishGridTotal(0);
                            });
                    } else return Promise.resolve();
                }

                Promise.all([innerGetTotal(), initTable(pageSize, startRow, sortInfo)])
                    .then(([_, result]) => {
                        const { entities, total, lastRow } = result;

                        // Because "total" is only real in the first call. After that it's always 0.
                        if (isCurrentRequest() && !getTotal) {
                            if (entities.length > 0 && total) {
                                publishGridTotal(total);
                            }
                        }

                        if (isCurrentRequest() && !entities?.length && startRow === 0) {
                            gridContext.current.totalData = 0;
                        }

                        // Keep loaded rows available for overlay decisions. An empty later block
                        // must not erase evidence that the table already contains painted rows.
                        if (startRow === 0 || entities.length > 0) {
                            immutableRowData.current = [...entities];
                        }

                        // Cell renderers read favoriteData while AG Grid creates the block.
                        // Populate it before successCallback so the initial pinned cells do
                        // not need to be recycled by scrolling before showing their state.
                        newGetFavoriteRows(entities);
                        // Checkbox cell renderers need this state while AG Grid creates each row.
                        // Waiting for the painted-row callback causes the initial block to appear
                        // unchecked until a later refresh or scroll.
                        if (isCurrentRequest()) {
                            newGetCheckedRows();
                        }
                        // When adding sections with fullWidthRow, the table doesn't control well the total rows to print,
                        // >> so we control it using the grigContext.current.table data to force to not print more rows
                        // >> than necessary in the last pagination
                        if (useLazyWithSections) {
                            if (startRow === 0) gridContext.current.tableDataForLazySections = [];
                            const isLastPage = entities.length < pageSize;

                            if (isLastPage) {
                                params.successCallback(
                                    entities,
                                    gridContext.current.tableDataForLazySections.length +
                                        entities.length,
                                );
                            } else {
                                params.successCallback(entities);
                            }
                            gridContext.current.tableDataForLazySections = [
                                ...gridContext.current.tableDataForLazySections,
                                ...entities,
                            ];
                        } else if (infiniteScrolling) {
                            params.successCallback(entities, lastRow);
                        } else {
                            const knownTotal = gridContext.current.totalData ?? 0;

                            if (!getTotal && !knownTotal) {
                                let lastRowIndex;
                                if (!entities.length) {
                                    lastRowIndex = startRow;
                                } else if (entities.length < pageSize) {
                                    lastRowIndex = startRow + entities.length;
                                }
                                params.successCallback(entities, lastRowIndex);
                            } else {
                                params.successCallback(
                                    entities,
                                    !getTotal ||
                                        entities.length < pageSize ||
                                        pageSize === knownTotal
                                        ? getTotalWithFullWidthRows(entities, knownTotal)
                                        : undefined,
                                );
                            }
                        }

                        if (!entities?.length) {
                            if (isCurrentRequest()) {
                                finalizeEmptyLazyLoad(startRow);
                            }
                            return 'empty-finalized';
                        }

                        return true;
                    })
                    .catch((error) => {
                        if (error === 'aborted') {
                            params.failCallback();
                            return false;
                        }
                        if (error) console.error(error);
                        if (startRow > 0) {
                            params.failCallback();
                            return false;
                        }
                        if (isCurrentRequest()) {
                            immutableRowData.current = [];
                        }
                        // On the initial block, keep the grid shell and show the no-rows overlay.
                        params.successCallback([], 0);
                        if (isCurrentRequest()) {
                            finalizeEmptyLazyLoad(startRow);
                        }
                        return 'empty-finalized';
                    })
                    .then((shouldFinishLoad) => {
                        if (
                            !shouldFinishLoad ||
                            shouldFinishLoad === 'empty-finalized' ||
                            !isCurrentRequest()
                        ) {
                            return;
                        }

                        // Finally callback
                        if (getForceRedrawRows?.()) {
                            // This method is to force the table to ignore/forget cache memory in the rendering so,
                            // >> we ignore the result of the purgeInfiniteCache that ignored the redraw of several
                            // >> rows besides those were updated, but didn't reflected the change on the table
                            // This is very specific for the case where we have a table with fullWidthRows and useLazyLoad (inifinite scroll)
                            // >> and we need to refresh the table without a mounting the component again or populating it with a complete different data.
                            // In short. Without this, the table makes a refresh, but the fullWidthRows aren't displayed as fullWidthRows but normal rows.
                            gridApi?.current?.redrawRows();
                        }

                        if (startRow > 0) {
                            return;
                        }

                        const finishLoad = () => {
                            clearGridLoadingState();
                            setLazyLoaded(true);
                            newGetFavoriteRows();
                            if (
                                (gridContext.current.hasShowTotal || forceGetTotal) &&
                                startRow === 0
                            ) {
                                publishGridTotal(gridContext.current.totalData);
                            }

                            dataGridRef.current?.classList?.remove(classes.isEmpty);
                            dataGridRef.current?.classList?.remove(classes.isLoading);
                            gridApi?.current?.setGridOption('suppressNoRowsOverlay', true);
                            gridApi?.current?.setGridOption('loading', false);
                            hideGridOverlay(gridApi.current);
                        };

                        cancelPendingReveal();
                        pendingRevealCancelRef.current = waitForGridBodyRowsPainted({
                            root: dataGridRef.current,
                            onReady: () => {
                                pendingRevealCancelRef.current = null;
                                finishLoad();
                            },
                        });
                    });
            },
        };
        gridApi.current.setGridOption('datasource', dataSource);
    }, [
        initTable,
        pageSize,
        classes.isEmpty,
        classes.isLoading,
        cancelPendingReveal,
        clearGridLoadingState,
        finalizeEmptyLazyLoad,
        getTotal,
        setLazyLoaded,
        useLazyWithSections,
        infiniteScrolling,
        getTotalWithFullWidthRows,
        getForceRedrawRows,
        newGetFavoriteRows,
        newGetCheckedRows,
        forceGetTotal,
        showLoadingStateOverlay,
        publishGridTotal,
    ]);

    const onReady = useCallback(
        (params) => {
            if (initOnReady) {
                if (useLazyLoad) {
                    setGridDataSource();
                } else initTable(pageSize);
            }
        },
        [initOnReady, useLazyLoad, initTable, setGridDataSource, pageSize],
    );

    // Main events
    const onGridReady = useCallback(
        (params) => {
            gridRef.current = params;
            gridApi.current = params.api;
            // AG Grid v33+ merged the Column API into the Grid API; params.columnApi no longer exists.
            gridColumnApi.current = params.api;
            pendingColumnVisibilityRef.current = null;
            columnVisibilitySignatureRef.current = '';
            setGridReadyEpoch((epoch) => epoch + 1);
            syncColumnVisibilityFromProps();
            syncGridOverlay();

            if (fieldSelector) {
                applyFieldSelectorSpacerColumnState(params.api, columnDefs);
            }

            addDropZones && addDropZones(params);
            onReady && onReady(params);
            if (useDragCustom) {
                dragObserver.current?.disconnect();
                dragObserver.current = addCustomDrag({
                    getCustomDragInfo,
                    immutableRowData: immutableRowData,
                    rowData,
                    dragPreview: customDragPreview,
                    previewKey,
                    canDrop,
                    onDragDrop,
                    dragTarget,
                    canDrag,
                });
            }
        },
        [
            addDropZones,
            columnDefs,
            onReady,
            syncColumnVisibilityFromProps,
            syncGridOverlay,
            fieldSelector,
            useDragCustom,
            getCustomDragInfo,
            rowData,
            customDragPreview,
            previewKey,
            canDrop,
            onDragDrop,
            canDrag,
        ],
    );

    const onGridSizeChanged = useCallback(
        (params) => {
            const clientWidth = gridWidthRef.current;
            gridWidthRef.current = params.clientWidth;
            if (params.clientWidth && clientWidth && params.clientWidth === clientWidth) return;

            // Scrollbar appearance after a manual resize changes clientWidth and would
            // reset user widths via sizeColumnsToFit (ProductLinesTable flicker).
            if (userResizedColumnIdsRef.current.size > 0) return;

            // Only skip when the parent passed explicit widths (e.g. cached sizes). AG Grid
            // assigns default widths on its column defs, which must not block autofit.
            if (hasExplicitColumnWidthsFromDefs(columnDefs)) return;

            clearTimeout(sizeColumnsToFitTimerRef.current);
            sizeColumnsToFitTimerRef.current = setTimeout(() => {
                sizeColumnsToFitTimerRef.current = null;
                try {
                    params?.api?.sizeColumnsToFit?.();
                    if (fieldSelector) {
                        applyFieldSelectorSpacerColumnState(params.api, columnDefs);
                    }
                } catch (e) {
                    console.error(e);
                }
            });
        },
        [columnDefs, fieldSelector],
    );

    const onFirstDataRendered = useCallback(
        (params) => {
            try {
                if (!saveColumnSize && !hasExplicitColumnWidthsFromDefs(columnDefs)) {
                    params?.api?.sizeColumnsToFit?.();
                }
                if (fieldSelector) {
                    applyFieldSelectorSpacerColumnState(params.api, columnDefs);
                }
                apiRef.current = params.api;
                syncGridOverlay();
                completeInitialColumnLayout();
            } catch (e) {
                console.error(e);
            }
        },
        [columnDefs, saveColumnSize, syncGridOverlay, fieldSelector, completeInitialColumnLayout],
    );

    const moveInArray = useCallback((arr, fromIndex, toIndex) => {
        const element = arr[fromIndex];
        arr.splice(fromIndex, 1);
        arr.splice(toIndex, 0, element);
    }, []);

    const onRowDragEnter = useCallback(
        (event) => {
            if (showGhostWhenDrag) {
                const ghostRow = document.querySelector('.ag-dnd-ghost');
                if (ghostRow) ghostRow.classList.add('ag-dnd-ghost--visible');
            }
            isDragging.current = true;
            setDraggedRow(event.api, event.node, true);
            removeChildrenOnDrag(
                event.api,
                onCancelGroupRequests,
                commitGridRowData,
                gridContext.current.tableData,
            );

            immutableBeforeDrag.current = [];
            event.api.forEachNode((current) => {
                immutableBeforeDrag.current.push(current.data);
            });

            addToRowsBeforeDrag(immutableBeforeDrag.current);
            onDragEnter && onDragEnter(event);
        },
        [
            isDragging,
            onCancelGroupRequests,
            immutableBeforeDrag,
            onDragEnter,
            showGhostWhenDrag,
            commitGridRowData,
        ],
    );

    const onRowDragMove = useCallback(
        (event) => {
            const movingNode = event.node;
            const overNode = event.overNode;
            const rowNeedsToMove = movingNode !== overNode;
            if (rowNeedsToMove) {
                const movingData = movingNode?.data;
                const overData = overNode?.data;
                if (!overData) return;
                if (dragGroups?.groups && dragGroups?.groupsKey) {
                    const movingDataKey = movingData[dragGroups.groupsKey];
                    const group = dragGroups?.groups[movingDataKey];
                    if (movingDataKey && group && !group.includes(overData.id)) {
                        return;
                    }
                }
                const offset = gridApi.current.getFirstDisplayedRowIndex();
                const fromIndex = immutableRowData.current.indexOf(movingData);
                let toIndex = immutableRowData.current.indexOf(overData) + offset;
                if (withoutOffset) toIndex = immutableRowData.current.indexOf(overData);
                const newRowData = immutableRowData.current.slice();
                moveInArray(newRowData, fromIndex, toIndex);
                immutableRowData.current = newRowData;
                rowMoved.current = true;
                updateTableDataRefs(newRowData);
                gridApi.current.setGridOption('rowData', newRowData);
                gridApi.current.clearFocusedCell();
            }
        },
        [dragGroups, moveInArray, withoutOffset, updateTableDataRefs],
    );

    const onRowDragEnd = useCallback(
        (event) => {
            if (rowMoved.current && onDragEnd) {
                const data = { rowDataBefore: immutableBeforeDrag.current };
                addRequestToQueue(() => onDragEnd(event, data));

                if (!isQueueProcessing.current) {
                    isQueueProcessing.current = true;
                    processDragQueue(event, immutableRowData, isQueueProcessing);
                }
            }
            setDraggedRow(event.api, event.node);
            rowMoved.current = false;

            if (columnsRefreshOnDragEnd) {
                let columns = columnsRefreshOnDragEnd.filter((current) => {
                    return gridColumnApi.current.getColumn(current);
                });
                gridApi.current.refreshCells({ columns, force: true });
            }
        },
        [onDragEnd, columnsRefreshOnDragEnd],
    );

    const onRowDragLeave = useCallback((event) => {
        isDragging.current = false;
        setDraggedRow(event.api, event.node);
    }, []);

    const onColumnMoved = useCallback((event) => {
        const colId = getAgGridColumnId(event?.column) ?? getAgGridColumnId(event?.columns?.[0]);
        if (!colId || colId === FIELD_SELECTOR_SPACER_COL_ID) return;
        columnMovedId.current = colId;
        if (event?.toIndex != null) {
            columnMovedToIndex.current = event.toIndex;
        }
    }, []);

    const onColumnPinned = useCallback(
        (event) => {
            if (!savePinnedColumns || !event?.column) return;
            const colId = getAgGridColumnId(event.column);
            if (!colId) return;
            savePinnedColumns({
                colId,
                pinned: event.pinned ?? event.column.getPinned?.() ?? event.column.pinned,
            });
        },
        [savePinnedColumns],
    );

    const onColumnResized = useCallback(
        (event) => {
            const isUserResize =
                event?.source === 'uiColumnDragged' || event?.source === 'uiColumnResized';
            if (!isUserResize) return;

            getResizedColumnsFromEvent(event).forEach((column) => {
                const colId = getAgGridColumnId(column);
                if (!colId || colId === 'fmFieldSelectorSpacer') return;

                const width = getAgGridColumnWidth(column);
                if (width == null) return;

                userResizedColumnIdsRef.current.add(colId);
                userColumnWidthsRef.current[colId] = width;

                const sourceCol = columnDefs?.find((def) => (def.colId || def.field) === colId);
                if (sourceCol) sourceCol.width = width;

                applyUserColumnWidthToGridColDef(gridApi.current, colId, width, {
                    preserveFlexLastDataColumn: Boolean(actions?.length) && !fieldSelector,
                });
            });

            if (!event?.finished) return;

            clearTimeout(sizeColumnsToFitTimerRef.current);
            sizeColumnsToFitTimerRef.current = null;

            const widthItems = [];
            getResizedColumnsFromEvent(event).forEach((column) => {
                const colId = getAgGridColumnId(column);
                const width = getAgGridColumnWidth(column);
                if (!colId || width == null) return;

                widthItems.push({ key: colId, newWidth: width });
                saveColumnSize?.(colId, width);
            });

            if (widthItems.length) {
                gridApi.current?.setColumnWidths?.(widthItems);
            }

            if (fieldSelector) {
                applyFieldSelectorSpacerColumnState(gridApi.current, columnDefs);
            }

            setColumnLayoutEpoch((epoch) => epoch + 1);
        },
        [saveColumnSize, columnDefs, actions?.length, fieldSelector],
    );

    const onRowSelected = useCallback((event) => {
        if (event?.node?.isSelected?.()) {
            oldInitialActiveRowId.current = event.node.id;
        }
    }, []);

    const onViewportChanged = useCallback(() => {
        // To be executed when the table has to load with a row selected
        if (
            activeRowId &&
            (!oldInitialActiveRowId?.current || oldInitialActiveRowId.current !== activeRowId)
        ) {
            setRowSelected(activeRowId);
        }

        // Virtualized rows reuse cell DOM; resync checkbox visuals from selection state.
        refreshAllVisibleCheckboxes();
    }, [activeRowId, refreshAllVisibleCheckboxes, setRowSelected]);

    const onRowClicked = useCallback(
        (event) => {
            if (isDragging.current) {
                isDragging.current = false;
                return;
            }

            if (isDataGridCellNestedInteraction(event?.event?.target)) return;

            const rendererParams = event?.colDef?.cellRendererParams ?? {};
            if (
                rendererParams.onClick ||
                rendererParams.childrenParams?.onClick ||
                event?.colDef?.onCellClicked
            ) {
                return;
            }

            onClickRow && onClickRow(event);
        },
        [onClickRow],
    );

    const getIsAllChecked = useCallback((context, colId) => {
        const checkedInColumn = context?.checkboxData?.[colId]?.rows || null;
        const checkedList =
            checkedInColumn &&
            Object.keys(checkedInColumn).filter((current) => {
                return checkedInColumn[current];
            });

        // checkedList contains all the rows checked of the loaded rows
        if (checkedList.length === context.totalData) {
            context.checkboxData[colId].areAllChecked = true;
        } else context.checkboxData[colId].areAllChecked = false;
    }, []);

    const getIsRowChecked = useCallback(
        (colId, rowId) => {
            const context = gridContext.current;
            let isChecked;

            if (useLazyLoad) {
                if (context.checkboxData[colId].rows[rowId]) {
                    isChecked = context.checkboxData[colId].rows[rowId];
                } else if (
                    typeof context.checkboxData[colId].rows?.[rowId] === 'boolean' &&
                    !context.checkboxData[colId].rows?.[rowId]
                ) {
                    isChecked = false;
                } else if (context.checkboxData[colId].lazyAreAllChecked) {
                    isChecked = true;
                } else {
                    isChecked = false;
                }
            } else {
                isChecked = context.checkboxData[colId].rows[rowId] || false;
            }

            return isChecked;
        },
        [useLazyLoad],
    );

    // Here we build the resultant checkboxData after checkbox click
    const onClickCheckbox = useCallback(
        (checkboxCallback) => {
            return ({ id, colId, boolean, context, column, rowIndex, data }) => {
                if (column && !colId) return;
                else if (!colId && !id) return;

                let totalBlocked = 0;
                if (!context?.checkboxData) context.checkboxData = {};
                if (!context?.checkboxData?.[colId]) {
                    context.checkboxData[colId] = {};
                    context.checkboxData[colId].rows = {};
                }
                if (column) {
                    let newCheckboxes = {};
                    gridApi.current.forEachNode((node) => {
                        if (disabledCheckboxes?.[colId]?.[node.id]) return;
                        if (
                            node?.data?.oldSelected === '1' &&
                            !column.colDef.headerComponentParams?.checkbox?.allowDeselectOldSelected
                        ) {
                            newCheckboxes[node.id] = true;
                            return;
                        }
                        if (
                            column.colDef.cellRendererParams?.checkbox?.isDisabled &&
                            column.colDef.cellRendererParams?.checkbox?.isDisabled(
                                colId,
                                id,
                                node,
                            ) &&
                            column.colDef.cellRendererParams?.checkbox?.noCheckedWhenDisabled
                        ) {
                            totalBlocked++;
                            return;
                        }
                        newCheckboxes[node.id] = boolean;
                    });
                    context.checkboxData[colId].areAllChecked = boolean;
                    context.checkboxData[colId].rows = newCheckboxes;
                    if (useLazyLoad) {
                        context.checkboxData[colId].lazyAreAllChecked = boolean;
                    }
                } else {
                    context.checkboxData[colId].rows[id] = boolean;

                    if (useLazyLoad) {
                        getIsAllChecked(context, colId);
                        context.checkboxData[colId].lazyAreAllChecked =
                            context.checkboxData[colId].lazyAreAllChecked || false;
                    } else if (!boolean) {
                        context.checkboxData[colId].areAllChecked = false;
                    } else {
                        getIsAllChecked(context, colId);
                    }
                }

                const areAllChecked = context?.checkboxData[colId]?.areAllChecked || null;

                checkboxCallback &&
                    checkboxCallback({
                        id: id || null,
                        colId,
                        isColumn: !!column,
                        value: boolean,
                        checked: context.checkboxData,
                        rowIndex,
                        data,
                        total: areAllChecked ? context.totalData - totalBlocked : 0,
                    });

                if (column) {
                    refreshCheckboxes(colId, areAllChecked);
                } else {
                    refreshHeaderCheckboxes(colId);
                }
            };
        },
        [
            useLazyLoad,
            disabledCheckboxes,
            getIsAllChecked,
            refreshCheckboxes,
            refreshHeaderCheckboxes,
        ],
    );

    const getIsRowFavorite = useCallback(
        (colId, rowId) => {
            const context = gridContext.current;
            let isFavorite;

            if (useLazyLoad) {
                if (context.favoriteData[colId].rows[rowId]) {
                    isFavorite = context.favoriteData[colId].rows[rowId];
                } else if (
                    typeof context.favoriteData[colId].rows?.[rowId] === 'boolean' &&
                    !context.favoriteData[colId].rows?.[rowId]
                ) {
                    isFavorite = false;
                } else {
                    isFavorite = false;
                }
            } else {
                isFavorite = context.favoriteData[colId].rows[rowId] || false;
            }

            return isFavorite;
        },
        [useLazyLoad],
    );

    const onClickFavorite = useCallback((favoriteCallback) => {
        return ({ id, colId, boolean, context, rowIndex, data }) => {
            if (!colId && !id) return;

            if (!context?.favoriteData) context.favoriteData = {};
            if (!context?.favoriteData?.[colId]) {
                context.favoriteData[colId] = {};
                context.favoriteData[colId].rows = {};
            }

            context.favoriteData[colId].rows[id] = boolean;

            favoriteCallback &&
                favoriteCallback({
                    id: id || null,
                    colId,
                    value: boolean,
                    checked: context.favoriteData,
                    rowIndex,
                    data,
                });
        };
    }, []);

    const newFullWidthCellRender = useMemo(() => {
        if (useFullWidthDivider) return FullWidthDividerCell;
        else {
            return fullWidthCellRenderer && !onCancelGroupRequests
                ? fullWidthCellRenderer
                : LoadingCell;
        }
    }, [fullWidthCellRenderer, onCancelGroupRequests, useFullWidthDivider]);

    const loadingIsFullWidthCell = useCallback((rowNode) => {
        return rowNode?.data?.isFullWidth || false;
    }, []);

    const isFullWidthRow = useCallback(
        ({ rowNode }) => {
            if (customIsFullWidthCell)
                return customIsFullWidthCell(rowNode) || loadingIsFullWidthCell(rowNode);
            else return loadingIsFullWidthCell(rowNode);
        },
        [customIsFullWidthCell, loadingIsFullWidthCell],
    );

    const finalRowHeight = coerceNumericGridOption(props.rowHeight) || defaultOptions.rowHeight;

    const getRowHeight = useCallback(
        (params) => {
            return params?.data?.isLoader ? 150 : finalRowHeight;
        },
        [finalRowHeight],
    );

    const customRowCells = useMemo(
        () => ({
            textCell: TextCell,
            textTwoValuesCell: TextTwoValuesCell,
            avatarCell: AvatarCell,
            avatarCellWithSyncStatus: AvatarCellWithSyncStatus,
            dateCell: DateCell,
            currencyCell: CurrencyCell,
            valuePercentageCell: ValuePercentageCell,
            percentageCell: PercentageCell,
            percentageTextCell: PercentageTextCell,
            booleanCell: BooleanCell,
            booleanIconCell: BooleanIconCell,
            numberCell: NumberCell,
            rowNumberCell: RowNumberCell,
            toggleCell: ToggleCell,
            tagCell: TagCell,
            iconCell: IconCell,
            multiValueCell: MultiValueCell,
            MultiSelectReadCell,
            stepCell: StepCell,
            filterCell: FilterCell,
            fullWidthCellRenderer: newFullWidthCellRender,
            textFilter: TextFilter,
            headerTextCell: HeaderTextCell,
            headerIconCell: HeaderIconCell,
            headerBlankCell: HeaderBlankCell,
        }),
        [newFullWidthCellRender],
    );

    const wrappedChangeSortDirection = useCallback(
        (props) => {
            changeSortDirection && changeSortDirection(props);
        },
        [changeSortDirection],
    );

    const newChangeSortDirection = useCallback((getSortInfoForLazyLoad) => {
        return (sortModel) => {
            if (getSortInfoForLazyLoad) getSortInfoForLazyLoad(sortModel);
        };
    }, []);

    useEffect(() => {
        columnDefs?.forEach((col) => {
            const colId = col.colId || col.field;
            if (!colId || col.width == null) return;
            if (!userResizedColumnIdsRef.current.has(colId)) {
                userColumnWidthsRef.current[colId] = col.width;
            }
        });
    }, [columnDefs]);

    useLayoutEffect(() => {
        if (!columnDefs?.length || !gridApi.current) return;

        columnDefs.forEach((col) => {
            const colId = col.colId || col.field;
            if (!colId) return;

            const userWidth = userColumnWidthsRef.current[colId];
            if (userWidth != null) {
                col.width = userWidth;
            }
        });

        syncUserColumnWidthsToGridColDefs(
            gridApi.current,
            userColumnWidthsRef.current,
            userResizedColumnIdsRef.current,
            { preserveFlexLastDataColumn: Boolean(actions?.length) && !fieldSelector },
        );
    }, [columnDefs, actions?.length, fieldSelector]);

    const finalColumnDefs = useMemo(() => {
        const dataColumnDefs = columnDefs
            .filter((col) => (col.colId || col.field) !== FIELD_SELECTOR_SPACER_COL_ID)
            .map((col) => ({
                ...col,
                cellRendererParams: { ...col.cellRendererParams },
                headerComponentParams: { ...col.headerComponentParams },
                hide: resolveColumnHide(col),
            }));
        const lastVisibleDataIdx = getLastVisibleDataColumnIndex(dataColumnDefs);

        const mapped = dataColumnDefs.map((current, idx) => {
            const colId = current.colId || current.field;
            const isLastVisibleDataColumn = idx === lastVisibleDataIdx;
            const userWidth = colId ? userColumnWidthsRef.current[colId] : null;
            if (userWidth != null) {
                current.width = userWidth;
            }

            // If we have actions but the current column doesn't use a customCellRenderer, we set one
            // because the actions are controlled from the Base cell in the customCells
            if (
                actions?.length > 0 &&
                (!current.cellRenderer ||
                    (!customRowCells[current.cellRenderer] &&
                        !componentsRef.current[current.cellRenderer]))
            ) {
                current.cellRenderer = 'textCell';
            }

            if (!current.cellRendererParams) current.cellRendererParams = {};
            // Locale only — do not spread Web5Context here; useWeb5Context() returns a new
            // object every render and would force columnDefs to change on every parent render.
            current.cellRendererParams = {
                ...current.cellRendererParams,
                locale: resolvedLocale,
            };

            // AG Grid v33+ handles cell mouse events before custom renderers; opt out when the
            // cell defines its own click handler (e.g. folder navigation in documents/metrics).
            attachColDefCellClickHandler(current);

            if (!current.headerComponentParams) current.headerComponentParams = {};

            if (useDragColumns && current.rowDrag) {
                current.lockPosition = true;
                current.suppressMovable = true;
            } else if (useDragColumns && !current.lockPosition && !current.rowDrag) {
                if (!current.headerComponent) current.headerComponent = 'headerTextCell';
                if (!current.suppressMovable) {
                    current.headerComponentParams = {
                        ...current.headerComponentParams,
                        useDragColumns: true,
                    };
                }
            }

            if (current.headerName && !current.headerComponentParams.displayName) {
                current.headerComponentParams.displayName = current.headerName;
            }

            if (useDragRows) {
                current.headerComponentParams = {
                    ...current.headerComponentParams,
                    useDragRows: true,
                };
            }

            if (useSort) {
                if (!current.headerComponent) current.headerComponent = 'headerTextCell';
                current.sortable = current.hasOwnProperty('sortable') ? current.sortable : true;
            } else if (useSort === false) {
                current.sortable = false;
            }

            const columnSortField =
                current.sortField ?? getColDefContextValue(current, 'sortField');

            if (useSort && columnSortField) {
                current.headerComponentParams.sortField = columnSortField;
            }

            if (useSort && changeSortDirection) {
                current.headerComponentParams.changeSortDirection = wrappedChangeSortDirection;
            }

            if (useSort) {
                if (getInitialSortInfo) {
                    // Lazy-load sort is driven by custom headers + applyColumnState; setting
                    // colDef.sort here would re-trigger getRows when Redux sort updates.
                    if (!useLazyLoad) {
                        const initialSort = stableGetInitialSortInfo();
                        if (
                            initialSort?.sortField &&
                            (columnSortField === initialSort.sortField ||
                                current.colId === initialSort.sortField)
                        ) {
                            current.sort = initialSort.sortDirection;
                        }
                    }
                    current.headerComponentParams.getInitialSortInfo = stableGetInitialSortInfo;
                }
                if (useLazyLoad) {
                    current.headerComponentParams.getSortInfoForLazyLoad =
                        newChangeSortDirection(getSortInfoForLazyLoad);
                }
                current.headerComponentParams.enableSorting = current.sortable !== false;
            }

            if (!current.minWidth) {
                current.minWidth =
                    current?.width < defaultColumnMinWidth ? current.width : defaultColumnMinWidth;
            }

            if (current?.headerComponentParams?.showTotal) {
                gridContext.current.hasShowTotal = true;
            }

            if (current?.headerComponentParams?.checkbox?.onChange) {
                const onChange = current.headerComponentParams.checkbox.onChange;
                delete current.headerComponentParams.checkbox.onChange;
                current.headerComponentParams.checkbox.onClick = onClickCheckbox(onChange);
            }
            if (current?.cellRendererParams?.checkbox?.onChange) {
                const onChange = current.cellRendererParams.checkbox.onChange;
                delete current.cellRendererParams.checkbox.onChange;
                current.cellRendererParams.checkbox.onClick = onClickCheckbox(onChange);
                current.cellRendererParams.checkbox.getIsRowChecked = getIsRowChecked;
            }
            if (current?.cellRendererParams?.favorite?.onChange) {
                const onChange = current.cellRendererParams.favorite.onChange;
                delete current.cellRendererParams.favorite.onChange;
                current.cellRendererParams.favorite.onClick = onClickFavorite(onChange);
                current.cellRendererParams.favorite.getIsRowFavorite = getIsRowFavorite;
            }

            const headerClass = current.headerClass;
            const headerClassNames = ['w5-header'];

            if (current.headerComponent) {
                headerClassNames.push('w5-header-custom-cell');
            }

            const align = current.cellRendererParams.align;
            const alignHeader = current.cellRendererParams.alignHeader;

            if (alignHeader) headerClassNames.push(`w5-header--${alignHeader}`);
            else if (align) headerClassNames.push(`w5-header--${align}`);

            const removeHover = current.headerComponentParams.removeHover;
            if (!removeHover) headerClassNames.push('w5-header--hover');

            current.cellClassRules = {
                'w5-cell-container': function (params) {
                    return true;
                },
                // Under this class we can edit native classes of the
                // >> table without breaking the styles of the native cellRenderer
                'w5-cell-container-custom-cell': function (params) {
                    if (current.cellRenderer) return true;
                    else return false;
                },
                'w5-cell-container-drag': function (params) {
                    return useDragRows;
                },
                'w5-cell-container-no-drag': function (params) {
                    return !useDragRows;
                },
                'w5-cell-container--dragging': function (params) {
                    if (draggedRow) {
                        return (
                            draggedRow?.data?.id &&
                            params?.data?.id &&
                            draggedRow.data.id === params.data.id
                        );
                    } else return false;
                },
                'w5-cell-container-lazy-drag-custom': function (params) {
                    return useDragCustom;
                },
                'w5-cell-container-row-drag-disabled': function (params) {
                    if (!useDragRows || !params?.colDef?.rowDrag) return false;
                    return !isRowDragEnabled(params);
                },
                'w5-cell-container--no-border-left': function (params) {
                    return params?.colDef?.cellRendererParams?.isLast || false;
                },
                'w5-cell-container--no-border-right': function (params) {
                    return params?.colDef?.cellRendererParams?.isFirst || false;
                },
                'w5-cell-container--no-border-left-and-right': function (params) {
                    if (
                        !params?.colDef?.cellRendererParams?.isFirst &&
                        !params?.colDef?.cellRendererParams?.isLast &&
                        params.colDef.cellRenderer === 'stepCell'
                    )
                        return true;
                    else return false;
                },
                ...current.cellClassRules,
            };

            if (current.lockPosition) {
                current.suppressMovable = current.suppressMovable ?? true;
                current.lockPinned = current.lockPinned ?? true;
            }

            if (fieldSelector && isLastVisibleDataColumn) {
                headerClassNames.push('w5-header--field-selector-last-data');
            }

            const uniqueHeaderClassNames = Array.from(new Set(headerClassNames));
            current.headerClass = (params) => {
                const configuredHeaderClass =
                    typeof headerClass === 'function' ? headerClass(params) : headerClass;
                const configuredHeaderClassNames = (
                    Array.isArray(configuredHeaderClass)
                        ? configuredHeaderClass
                        : [configuredHeaderClass]
                ).flatMap((className) => className?.split?.(' ') || []);

                return Array.from(
                    new Set([...configuredHeaderClassNames, ...uniqueHeaderClassNames]),
                );
            };

            // Keep user/cache widths when auto-fitting (onFirstDataRendered / onGridSizeChanged).
            const columnHasExplicitWidth =
                colId &&
                (userResizedColumnIdsRef.current.has(colId) ||
                    userColumnWidthsRef.current[colId] != null);
            const shouldFlexFillLastColumn =
                isLastVisibleDataColumn && (actions?.length > 0 || !columnHasExplicitWidth);

            if (columnHasExplicitWidth && !shouldFlexFillLastColumn) {
                current.suppressSizeToFit = true;
                if (userResizedColumnIdsRef.current.has(colId)) {
                    delete current.flex;
                }
            }

            // Last visible column fills remaining grid width (actions strip, or single
            // custom-row columns such as Products / AddProductsTable).
            if (shouldFlexFillLastColumn) {
                current.suppressSizeToFit = false;
                if (fieldSelector && isLastVisibleDataColumn) {
                    // Flex hides the resize handle in AG Grid v35; sizeColumnsToFit fills width instead.
                    current.resizable = true;
                    delete current.flex;
                } else {
                    if (actions?.length) {
                        current.resizable = false;
                    }
                    current.flex = 1;
                    if (current.width != null) {
                        current.minWidth = Math.max(current.minWidth ?? 0, current.width);
                        delete current.width;
                    }
                }
            }

            if (fieldSelector && isLastVisibleDataColumn && current.resizable !== false) {
                current.resizable = true;
            }
            return sanitizeColDefForAgGrid(current);
        });

        return fieldSelector
            ? [
                  ...mapped,
                  sanitizeColDefForAgGrid(
                      getFieldSelectorSpacerColDef({
                          fill: hasNoVisibleFieldSelectorColumns,
                      }),
                  ),
              ]
            : mapped;
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- epoch intentionally invalidates ref-derived column widths
    }, [
        actions,
        changeSortDirection,
        columnDefs,
        components,
        customRowCells,
        resolvedLocale,
        getInitialSortInfo,
        stableGetInitialSortInfo,
        getIsRowChecked,
        getIsRowFavorite,
        getSortInfoForLazyLoad,
        wrappedChangeSortDirection,
        newChangeSortDirection,
        onClickCheckbox,
        onClickFavorite,
        useDragColumns,
        useDragRows,
        useDragCustom,
        useLazyLoad,
        useSort,
        columnLayoutEpoch,
        fieldSelector,
        hasNoVisibleFieldSelectorColumns,
        resolveColumnHide,
    ]);

    const onDragStopped = useCallback(
        (event) => {
            if (!saveColumnsOrder) {
                columnMovedId.current = null;
                columnMovedToIndex.current = null;
                return;
            }
            if (saveColumnsOrder && columnMovedId.current) {
                const api = gridColumnApi.current ?? event?.api;
                const movedId = columnMovedId.current;
                const toIndex = columnMovedToIndex.current;
                const stateOrder =
                    api
                        ?.getState?.()
                        ?.columnOrder?.orderedColIds?.filter(
                            (colId) => colId && colId !== FIELD_SELECTOR_SPACER_COL_ID,
                        ) ??
                    api
                        ?.getColumnState?.()
                        ?.map((col) => col.colId)
                        ?.filter((colId) => colId && colId !== FIELD_SELECTOR_SPACER_COL_ID) ??
                    [];
                const columnOrder =
                    movedId != null && toIndex != null && stateOrder.length
                        ? applyColumnMoveToIndex(stateOrder, movedId, toIndex)
                        : stateOrder.length
                          ? stateOrder
                          : (api
                                ?.getAllDisplayedColumns?.()
                                ?.map((column) => getAgGridColumnId(column))
                                ?.filter(
                                    (colId) => colId && colId !== FIELD_SELECTOR_SPACER_COL_ID,
                                ) ?? []);
                const indexOfMoved = columnOrder.indexOf(movedId);
                const columnAfter = indexOfMoved > 0 ? columnOrder[indexOfMoved - 1] : null;
                const reorderedColumn = columnOrder[indexOfMoved] ?? movedId;
                saveColumnsOrder(columnAfter, reorderedColumn);
                columnMovedId.current = null;
                columnMovedToIndex.current = null;
                if (actions?.length) {
                    // fix last column props
                    const columns = gridApi.current.getColumnDefs();
                    const lastDataIdx = getLastVisibleDataColumnIndex(columns);
                    columns.forEach((column, idx) => {
                        if (idx === lastDataIdx) {
                            if (fieldSelector) {
                                column.resizable = true;
                                delete column.flex;
                            } else {
                                column.resizable = false;
                                column.flex = 1;
                            }
                            column.suppressSizeToFit = false;
                            if (column.width != null) {
                                column.minWidth = Math.max(column.minWidth ?? 0, column.width);
                            }
                            if (!fieldSelector) {
                                column.width = undefined;
                            }
                        } else {
                            delete column.flex;
                            const originalColumn = columnDefs.find(
                                (def) => def.colId === column.colId,
                            );
                            column.suppressSizeToFit = true;
                            column.resizable =
                                originalColumn?.hasOwnProperty('resizable') &&
                                reorderedColumn !== column.colId
                                    ? originalColumn.resizable
                                    : true;
                        }
                    });
                    gridApi.current.setGridOption('columnDefs', columns);
                }
                if (fieldSelector) {
                    applyFieldSelectorSpacerColumnState(gridApi.current, columnDefs);
                }
            }
        },
        [actions?.length, columnDefs, fieldSelector, saveColumnsOrder],
    );

    const onRowDataUpdated = useCallback(() => {
        if (forceRowUpdating) {
            gridApi.current?.refreshCells({ force: true });
        }
    }, [forceRowUpdating]);

    const loaderOverlayConfigRef = useRef({});
    loaderOverlayConfigRef.current = {
        id,
        useLoaderPlaceholder,
        placeholderOptions,
        loaderRowHeight: loaderPlaceholderRowHeight || finalRowHeight,
        loaderRows: loaderPlaceholderRows,
        loaderColumns: loaderPlaceholderColumns,
        useLoaderFullHeight: useLoaderPlaceholderFullHeight ?? useLoaderFullHeight,
    };

    const gridComponents = useMemo(
        () => ({
            ...customRowCells,
            ...vanillaComponents,
            loadingOverlay: (overlayProps) => (
                <LoadingOverlay
                    {...overlayProps}
                    {...loaderOverlayConfigRef.current}
                    gridContext={gridContext}
                />
            ),
            noRowsOverlay: customEmptyViewComponent || NoRowsOverlay,
            ...components,
        }),
        [customRowCells, vanillaComponents, customEmptyViewComponent, components],
    );

    const noRowsOverlayParamsRef = useRef({});
    noRowsOverlayParamsRef.current = {
        hasError,
        isLoading,
        emptyViewProps,
        errorViewProps,
    };

    const noRowsOverlayComponentParams = useMemo(
        () => ({
            get hasError() {
                return noRowsOverlayParamsRef.current.hasError;
            },
            get isLoading() {
                return noRowsOverlayParamsRef.current.isLoading;
            },
            get emptyViewProps() {
                return noRowsOverlayParamsRef.current.emptyViewProps;
            },
            get errorViewProps() {
                return noRowsOverlayParamsRef.current.errorViewProps;
            },
            headerHeight: 40,
        }),
        [],
    );

    const clientRowData = !useLazyLoad ? (committedRowData ?? rowData) : undefined;

    // Derived from props — must not default to true on remount (e.g. tab switch) or the empty
    // overlay stays suppressed until a manual showNoRowsOverlay() wins the race.
    const suppressNoRowsOverlayOption = isLoading || (useLazyLoad && !isLazyLoadedRef.current);

    // Base grid options. These sit BELOW the consumer's `...props` on the grid, so any
    // option here can be overridden by props (matches the previous `...defaultOptions ->
    // internal -> ...props` order). `props` is intentionally excluded from this memo so the
    // options object stays referentially stable across renders when the real inputs below
    // do not change (the rest object `props` changes identity every render).
    const baseOptions = useMemo(() => {
        // columnDefs / rowData are passed as dedicated props after this spread — do not
        // include the empty defaults from defaultOptions or they would wipe the grid.
        const {
            columnDefs: _ignoredColumnDefs,
            rowData: _ignoredRowData,
            ...gridDefaults
        } = defaultOptions;

        const newOptions = {
            ...gridDefaults,
            rowSelection: {
                mode: 'singleRow',
                enableClickSelection: false,
                checkboxes: false,
                headerCheckbox: false,
            },
            rowModelType: useLazyLoad ? 'infinite' : null,
            cacheBlockSize: useLazyLoad ? pageSize : null,
            // Client-side: pre-render rows above/below the viewport for smoother scroll.
            // Infinite model: keep 0 to avoid a visible empty placeholder while getRows runs after sort/filter.
            rowBuffer: useLazyLoad ? 0 : 10,
            blockLoadDebounceMillis: useLazyLoad ? 0 : 300,
            maxConcurrentDatasourceRequests: 3,
            // Column virtualisation has no buffer. Horizontal scroll recreates cells for
            // every visible row per column — costly with our DOM-heavy renderers. Typical
            // CRM lists (<=25 cols) render faster with all columns mounted.
            suppressColumnVirtualisation: shouldSuppressColumnVirtualisation(
                columnDefs?.map((col) => {
                    const hide = resolveColumnHide(col);
                    return hide === col.hide ? col : { ...col, hide };
                }),
            ),
            tooltipShowDelay: 200,
            suppressNoRowsOverlay: suppressNoRowsOverlayOption,
            loading: showGridLoading ? true : false,
            loadingOverlayComponent: 'loadingOverlay',
            noRowsOverlayComponent: 'noRowsOverlay',
            fullWidthCellRenderer: 'fullWidthCellRenderer',
            isFullWidthRow,
            getRowHeight,
            rowClass: ['w5-row', rowClass, onClickRow && 'w5-row--click'].join(' '),
            rowClassRules: {
                'w5-row-loading': (params) => !params?.data,
                'w5-row-children': function (params) {
                    return params?.data?.isChildren;
                },
                'w5-row-loader': function (params) {
                    return params?.data?.isLoader;
                },
                'w5-row-group--open': function (params) {
                    if (params?.data?.isGroup && params?.data?.isGroupOpen) {
                        return true;
                    }
                },
                'w5-row-group--close': function (params) {
                    if (params?.data?.isGroup && !params?.data?.isGroupOpen) {
                        return true;
                    }
                },
                'w5-row-group--open__no-events': function (params) {
                    if (
                        params?.data?.isGroup &&
                        params?.data?.isGroupOpen &&
                        params?.data?.isLoading
                    ) {
                        return true;
                    }
                },
                'w5-row-full-width': function (params) {
                    return isFullWidthRow({ rowNode: params.node }) || false;
                },
                ...rowClassRules,
            },
            onRowDragEnter,
            onRowDragMove,
            onRowDragEnd,
            onRowDragLeave,
            onRowClicked,
            onDragStopped,
            onColumnMoved,
            onColumnPinned,
            onColumnResized,
            onViewportChanged,
            onRowSelected,
            onSortChanged: useLazyLoad && useSort !== false ? onLazyLoadSortChanged : undefined,
            onRowDataUpdated,
        };

        return newOptions;
    }, [
        columnDefs,
        resolveColumnHide,
        useLazyLoad,
        useSort,
        pageSize,
        suppressNoRowsOverlayOption,
        showGridLoading,
        isFullWidthRow,
        getRowHeight,
        rowClass,
        onClickRow,
        rowClassRules,
        onRowDragEnter,
        onRowDragMove,
        onRowDragEnd,
        onRowDragLeave,
        onRowClicked,
        onDragStopped,
        onColumnMoved,
        onColumnPinned,
        onColumnResized,
        onViewportChanged,
        onRowSelected,
        onLazyLoadSortChanged,
        onRowDataUpdated,
    ]);

    // Options that MUST win over consumer-provided props: drag behaviour, the lazy
    // datasource, and movable columns. These are spread AFTER `...props` on the grid so a
    // consumer cannot accidentally disable row drag or lazy loading. Kept in its own memo
    // (excluding `props`) so the object is referentially stable across renders when these
    // inputs do not change. Precedence preserved: baseOptions < props < overrideOptions.
    const overrideOptions = useMemo(() => {
        const affectedByDrag = {
            suppressRowDrag: true,
            getRowId: getRowNodeId
                ? ({ data }) => {
                      if (data == null) return '';
                      const rowId = getRowNodeId(data);
                      if (rowId != null && rowId !== '') {
                          return toAgGridRowId(rowId);
                      }
                      return resolveDefaultAgGridRowId(data);
                  }
                : ({ data }) => resolveDefaultAgGridRowId(data),
        };

        if (useDragRows) {
            affectedByDrag.animateRows = true;
            affectedByDrag.suppressRowDrag = false;
            affectedByDrag.rowDragManaged = false;
        }

        const result = { ...affectedByDrag };

        if (dataSource) {
            result.rowModelType = 'infinite';
            result.datasource = dataSource;
        }

        if (useDragColumns) {
            result.suppressMovableColumns = false;
        }

        if (useDragColumns || saveColumnsOrder) {
            result.maintainColumnOrder = true;
        }

        return result;
    }, [getRowNodeId, useDragRows, dataSource, useDragColumns, saveColumnsOrder]);

    const consumerGridPropsRef = useRef(props);
    consumerGridPropsRef.current = props;

    const { gridOptions: resolvedGridOptions, defaultColDefOverrides } = useMemo(
        () =>
            sanitizeGridOptionsForAgGrid({
                ...baseOptions,
                ...consumerGridPropsRef.current,
                ...overrideOptions,
            }),
        [baseOptions, overrideOptions],
    );

    const mergedDefaultColDef = useMemo(
        () => ({
            ...defaultOptions.defaultColDef,
            ...defaultColDef,
            ...defaultColDefOverrides,
            ...(useSort === false ? { sortable: false } : {}),
        }),
        [defaultColDef, defaultColDefOverrides, useSort],
    );

    useEffect(() => {
        if (
            gridRef &&
            gridRef.current &&
            gridRef.current.api &&
            gridRef.current.api.setRowCount &&
            infiniteInitialRowCount
        ) {
            gridRef.current.api.setRowCount(infiniteInitialRowCount);
        }

        return () => dragObserver.current?.disconnect();
    }, [infiniteInitialRowCount]);

    useEffect(() => {
        return () => {
            onCancelGroupRequests && onCancelGroupRequests();
        };
    }, [onCancelGroupRequests]);

    // Convert the grid columns schema to the format expected by ModalOptions
    const getModalOptionsSchema = useCallback(
        (groupedColumns) => {
            let newMappedSchema = [];

            if (groupedColumns?.length > 0) {
                newMappedSchema = groupedColumns.map((item) => {
                    const options = item.fields
                        .filter(
                            (option) => !getColDefContextValue(option, 'suppressFromFieldSelector'),
                        )
                        .map((option) => ({
                            id: option.colId,
                            label:
                                getColDefContextValue(option, 'fieldSelectorLabel') ??
                                option.headerName,
                        }));

                    return {
                        label: item.label || t('label_fields'),
                        id: item.label || t('label_fields'),
                        options: options,
                    };
                });
            }

            setMappedSchema([...newMappedSchema]);
        },
        [t],
    );

    const onChangeSelectedFields = useCallback((newSelectedFields) => {
        setSelectedFields({ ...newSelectedFields });
    }, []);

    const getSelectedFields = useCallback(
        (mappedSchema, hiddenColumns) => {
            const newSelectedFields = mappedSchema.reduce((obj, group) => {
                const options = group.options.reduce((fields, field) => {
                    if (hiddenColumns[field.id] && !hiddenColumns[field.id].hide)
                        fields.push(field.id);
                    return fields;
                }, []);

                obj[group.id] = {
                    isSelected:
                        (options.length > 0 && options.length === group.options.length) || false,
                    options,
                };

                return obj;
            }, {});

            oldSelectedFields.current = JSON.parse(JSON.stringify(newSelectedFields));
            onChangeSelectedFields(newSelectedFields);
        },
        [onChangeSelectedFields],
    );

    const { onCancel: handleCloseModalOptions, ...otherModalOptionsProps } = modalOptionsProps;

    const handleCancelExportModal = useCallback(() => {
        handleCloseModalOptions && handleCloseModalOptions(false);
        setSelectedFields(JSON.parse(JSON.stringify(oldSelectedFields.current)));
    }, [handleCloseModalOptions]);

    const handleConfirmExportModal = useCallback(
        (selectedFields) => {
            if (Object.keys(selectedFields).length > 0 && mappedSchema.length > 0) {
                oldSelectedFields.current = JSON.parse(JSON.stringify(selectedFields));

                const selectedColumns = Object.values(selectedFields).reduce(
                    (arr, { options }) => [...arr, ...options],
                    [],
                );

                let newSelectedFields = mappedSchema
                    .map((optionGroup) => optionGroup.options)
                    .flat()
                    .reduce(
                        (obj, item) => ({
                            ...obj,
                            [item.id]: selectedColumns.includes(item.id),
                        }),
                        {},
                    );

                if (
                    fieldSelectorLinkedColumnIds &&
                    typeof fieldSelectorLinkedColumnIds === 'object'
                ) {
                    const expanded = { ...newSelectedFields };
                    Object.entries(fieldSelectorLinkedColumnIds).forEach(([primary, linked]) => {
                        if (!Object.prototype.hasOwnProperty.call(expanded, primary)) return;
                        const visible = expanded[primary];
                        const linkedIds = Array.isArray(linked) ? linked : [];
                        linkedIds.forEach((colId) => {
                            expanded[colId] = visible;
                        });
                    });
                    newSelectedFields = expanded;
                }

                const columnState = Object.entries(newSelectedFields).map(([colId, visible]) => ({
                    colId,
                    hide: !visible,
                }));

                pendingColumnVisibilityRef.current = { ...newSelectedFields };

                gridApi.current?.applyColumnState({ state: columnState, applyOrder: false });

                columnVisibilitySignatureRef.current = buildColumnVisibilitySignature(
                    columnDefs,
                    resolveColumnHide,
                );

                const newHiddenColumns = { ...hiddenColumns };
                Object.entries(newSelectedFields).forEach(([column, visible]) => {
                    newHiddenColumns[column] = {
                        ...(hiddenColumns[column] || {
                            name:
                                getColDefContextValue(
                                    columnDefs.find((col) => col.colId === column),
                                    'fieldSelectorLabel',
                                ) ?? column,
                        }),
                        hide: !visible,
                    };
                });

                saveVisibleColumns?.(newSelectedFields);

                if (!saveVisibleColumns) {
                    columnDefs.forEach((column) => {
                        if (!column.colId || column.colId === 'fmFieldSelectorSpacer') return;
                        if (
                            !Object.prototype.hasOwnProperty.call(newSelectedFields, column.colId)
                        ) {
                            return;
                        }
                        column.hide = !newSelectedFields[column.colId];
                    });
                    pendingColumnVisibilityRef.current = null;
                }

                setColumnLayoutEpoch((epoch) => epoch + 1);

                if (fieldSelector && gridApi.current) {
                    applyFieldSelectorSpacerColumnState(gridApi.current, columnDefs);
                }

                setHiddenColumns(newHiddenColumns);
            }
            handleCloseModalOptions(false);
        },
        [
            columnDefs,
            fieldSelector,
            handleCloseModalOptions,
            hiddenColumns,
            mappedSchema,
            resolveColumnHide,
            saveVisibleColumns,
            fieldSelectorLinkedColumnIds,
        ],
    );

    useEffect(() => {
        if (isFirstLoad.current && fieldSelectorProps?.groupedColumns?.length > 0) {
            getModalOptionsSchema(fieldSelectorProps.groupedColumns);
            isFirstLoad.current = false;
        }

        if (hiddenColumns && Object.keys(hiddenColumns).length > 0) {
            getSelectedFields(mappedSchema, hiddenColumns);
        }
    }, [fieldSelectorProps, getModalOptionsSchema, getSelectedFields, hiddenColumns, mappedSchema]);

    const isFieldSelectorModalOpen = Boolean(otherModalOptionsProps?.isOpen);

    useEffect(() => {
        if (isFieldSelectorModalOpen && hiddenColumns && mappedSchema.length > 0) {
            getSelectedFields(mappedSchema, hiddenColumns);
        }
    }, [isFieldSelectorModalOpen, hiddenColumns, mappedSchema, getSelectedFields]);

    const clearHoveredRow = useCallback(() => setHoveredRowInfo(null), []);

    const showFieldSelectorMenu =
        fieldSelector && gridReadyEpoch > 0 && !isLoading && (!useLazyLoad || isLazyLoaded);

    return (
        <div id={id} data-w5-datagrid-root="" className={rootClassName} ref={dataGridRef}>
            {actions && actions?.length > 0 && (
                <Actions
                    actions={actions}
                    hideActions={hideActions}
                    hoveredRowInfo={hoveredRowInfo}
                    id={id}
                    dataGridRef={dataGridRef.current}
                    rowData={rowData}
                    isLazyLoaded={isLazyLoaded}
                    intercomRef={intercomRef}
                    isLoading={isLoading}
                    forceActionsRefresh={forceActionsRefresh}
                    clearHoveredRow={clearHoveredRow}
                />
            )}
            {showFieldSelectorMenu && (
                <MenuOptions
                    tooltipMenuContent={tooltipMenuContent}
                    fieldSelector={fieldSelector}
                    fieldSelectorProps={fieldSelectorProps}
                />
            )}
            <ToolTip tableId={id} />
            <Popover />
            <div className={classes.gridSurface}>
                <AgGridReact
                    ref={gridContext.current.tableRef}
                    onGridReady={onGridReady}
                    onGridSizeChanged={onGridSizeChanged}
                    onFirstDataRendered={onFirstDataRendered}
                    infiniteInitialRowCount={infiniteInitialRowCount}
                    {...resolvedGridOptions}
                    context={gridContext.current}
                    rowData={clientRowData}
                    columnDefs={finalColumnDefs}
                    defaultColDef={mergedDefaultColDef}
                    components={gridComponents}
                    noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                ></AgGridReact>
            </div>
            {Object.entries(otherModalOptionsProps).length > 0 && (
                <ModalOptions
                    schema={mappedSchema}
                    selected={selectedFields}
                    onCancel={handleCancelExportModal}
                    onChangeSelected={onChangeSelectedFields}
                    onConfirm={handleConfirmExportModal}
                    {...otherModalOptionsProps}
                />
            )}
        </div>
    );
}

DataGrid.propTypes = {
    className: PropTypes.string,
    fieldSelectorLinkedColumnIds: PropTypes.objectOf(
        PropTypes.arrayOf(PropTypes.string.isRequired),
    ),
};

export default React.memo(DataGrid);

let draggedRow = null;

function setDraggedRow(api, rowNode, isDragging) {
    draggedRow = (isDragging && rowNode) || null;
    let rowsToRefresh = [rowNode];
    refreshDraggedRow(api, rowsToRefresh);
}

function refreshDraggedRow(api, rowsToRefresh) {
    const params = {
        rowNodes: rowsToRefresh,
        force: true,
    };
    api.refreshCells(params);
}

function addCustomDrag({
    getCustomDragInfo,
    immutableRowData,
    rowData,
    dragPreview,
    previewKey,
    canDrop,
    onDragDrop,
    dragTarget,
    canDrag,
}) {
    const targetNode = queryGridCenterRowsContainer();
    const config = { childList: true };

    const rows = document.querySelectorAll('.w5-row');
    for (let row of rows) {
        addDragEvents({
            row,
            getCustomDragInfo,
            immutableRowData,
            rowData,
            dragPreview,
            previewKey,
            canDrop,
            onDragDrop,
            dragTarget,
            canDrag,
        });
    }

    const callback = (mutationsList, observer) => {
        mutationsList.forEach((mutation) => {
            if (mutation.type !== 'childList') return;
            mutation.addedNodes.forEach((node) => {
                addDragEvents({
                    row: node,
                    getCustomDragInfo,
                    immutableRowData,
                    rowData,
                    dragPreview,
                    previewKey,
                    canDrop,
                    onDragDrop,
                    dragTarget,
                    canDrag,
                });
            });
        });
    };

    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config);
    return observer;
}

function createDefaultDragPreview(event, previewElement, data, previewKey) {
    const previewStyles = {
        width: '125px',
        height: '35px',
        padding: '0px 10px',
        borderRadius: '2px',
        backgroundColor: '#5a89fd',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: '"Sage Text"',
        fontSize: '13px',
    };
    Object.assign(previewElement.style, previewStyles);

    const previewText = document.createElement('div');
    const previewTextStyles = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: 'white',
    };
    Object.assign(previewElement.style, previewTextStyles);
    previewText.innerHTML = data.name || data[previewKey];
    previewElement.appendChild(previewText);

    document.body.appendChild(previewElement);
    event.dataTransfer.setDragImage(previewElement, 0, 0);
}

function addDragEvents({
    row,
    rowData,
    getCustomDragInfo,
    immutableRowData,
    dragPreview,
    previewKey,
    canDrop,
    onDragDrop,
    dragTarget,
    canDrag,
}) {
    if (row.getAttribute('draggable')) return;
    const rowIndex = row.getAttribute('row-index');
    const data = immutableRowData.current?.[rowIndex] || rowData?.[rowIndex];
    if ((canDrag && canDrag(data)) || !canDrag) {
        row.setAttribute('draggable', true);
        row.addEventListener(
            'dragstart',
            function (event) {
                const previewElement = document.createElement('div');
                previewElement.setAttribute('id', 'w5-drag-preview');
                const rowId = row.getAttribute('row-id');
                const rowData = immutableRowData.current?.[row.getAttribute('row-index')];

                event.dataTransfer.setData('text/plain', JSON.stringify(rowData));
                event.dataTransfer.setData('id', rowId);

                if (dragPreview) {
                    const DragPreview = dragPreview;
                    const preview = renderToStaticMarkup(
                        <DragPreview data={rowData} previewKey={previewKey} />,
                    );
                    previewElement.innerHTML = preview;
                    document.body.appendChild(previewElement);
                    event.dataTransfer.setDragImage(previewElement, 0, 0);
                } else {
                    createDefaultDragPreview(event, previewElement, rowData, previewKey);
                }

                event.dataTransfer.dropEffect = 'move';
                getCustomDragInfo?.({ dragged: rowId });
            },
            false,
        );
    }

    row.addEventListener(
        'dragend',
        function (event) {
            const previewElement = document.getElementById('w5-drag-preview');
            previewElement?.remove();
            row.classList.remove('w5-drag-over');
            getCustomDragInfo?.({ dragged: null });
        },
        false,
    );

    row.addEventListener(
        'dragenter',
        function (event) {
            event.preventDefault();
            event.stopPropagation();
            if (event.currentTarget !== dragTarget.current) dragTarget.current = null;

            const rowData = immutableRowData.current?.[row.getAttribute('row-index')];
            const allowDrop = canDrop ? canDrop(rowData) : true;
            if (allowDrop) {
                row.classList.add('w5-drag-over');
            }
        },
        false,
    );

    row.addEventListener(
        'dragleave',
        function (event) {
            event.preventDefault();
            event.stopPropagation();
            const inRow = event.fromElement?.closest('.ag-row');
            if (event.currentTarget === dragTarget.current && inRow) return;
            if (!inRow) {
                dragTarget.current = event.currentTarget;
            } else dragTarget.current = event.currentTarget;
            row.classList.remove('w5-drag-over');
            getCustomDragInfo?.({ target: null });
        },
        false,
    );

    row.addEventListener(
        'drop',
        function (event) {
            const targetRow = event?.target?.closest('.ag-row');
            targetRow?.classList.remove('w5-drag-over');
            try {
                onDragDrop &&
                    onDragDrop(
                        JSON.parse(event.dataTransfer.getData('text/plain')),
                        immutableRowData.current?.[targetRow.getAttribute('row-index')],
                    );
            } catch (e) {
                console.error(e);
            }
        },
        false,
    );

    row.addEventListener(
        'dragover',
        function (event) {
            const rowId = row.getAttribute('row-id');
            const rowData = immutableRowData.current?.[row.getAttribute('row-index')];
            const allowDrop = canDrop ? canDrop(rowData) : true;
            if (allowDrop) {
                event.preventDefault();
                getCustomDragInfo?.({ target: rowId });
            }

            return false;
        },
        false,
    );
}
